import React, { useState, useEffect, useMemo } from "react";
import api from "../lib/api";
import moment from "moment";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import ReactPaginate from "react-paginate";
import CustomerPassbook from "./CustomerPassbook";
import "./CSS/ReportPremium.css";

import FuelFlowLogo from "./CSS/fuelFlowLogo.png";
import HPCLLogo from "./CSS/HPCL.png";
import NayaraLogo from "./CSS/NAYARA.png";
import BPCLLogo from "./CSS/BPCL.png";
import IOCLLogo from "./CSS/IOCL.png";
import JIOBPLogo from "./CSS/JIOBP.png";
import MRPLLogo from "./CSS/MRPL.png";
import ShellLogo from "./CSS/Shell.png";

// Charts
import {
  Area,
  AreaChart,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

// PDF export
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const NozzleReport = () => {
  const [activeTab, setActiveTab] = useState("nozzle");
  const token = localStorage.getItem("token");
  const readStoredValue = (key, fallback = "") => {
    const raw = localStorage.getItem(key);
    if (raw == null) return fallback;
    try {
      const parsed = JSON.parse(raw);
      return parsed || fallback;
    } catch (err) {
      return String(raw).replace(/^["']+|["']+$/g, "").trim() || fallback;
    }
  };
  const role = String(readStoredValue("role", "")).toLowerCase();
  const canViewPnl = role === "admin";
  const bunkName =
    readStoredValue("pump_name") ||
    readStoredValue("bunkname") ||
    "Fuel Station";
  const bunkAddress =
    readStoredValue("bunk_address") ||
    readStoredValue("address") ||
    readStoredValue("station_address") ||
    "Address not set";
  const adminPhone =
    readStoredValue("admin_phone") ||
    readStoredValue("phone") ||
    readStoredValue("mobile") ||
    "Phone not set";
  const companyName =
    readStoredValue("company_name") ||
    readStoredValue("company") ||
    "FuelFlow";
  const companyKey = String(companyName || "FuelFlow")
    .replace(/[^a-z0-9]+/gi, " ")
    .trim()
    .toUpperCase();
  const companyLogoMap = {
    BPCL: BPCLLogo,
    HPCL: HPCLLogo,
    IOCL: IOCLLogo,
    "JIO BP": JIOBPLogo,
    MRPL: MRPLLogo,
    NAYARA: NayaraLogo,
    SHELL: ShellLogo,
  };
  const activeCompanyLogo = companyLogoMap[companyKey] || FuelFlowLogo;
  const reportAccent =
    companyKey === "HPCL"
      ? "#ff7a00"
      : companyKey === "NAYARA"
      ? "#f15a24"
      : companyKey === "BPCL"
      ? "#f5c400"
      : companyKey === "IOCL"
      ? "#0052cc"
      : "#2563eb";
  const reportChartColors = [
    "#2563eb",
    "#16a34a",
    "#f59e0b",
    "#e11d48",
    "#06b6d4",
    "#8b5cf6",
    "#14b8a6",
    "#f97316",
  ];
  const formatAxisLabel = (value) =>
    String(value || "")
      .replace(/\b\w/g, (c) => c.toUpperCase())
      .slice(0, 14);

  const formatDateTimeDisplay = (value) =>
    value ? moment(value).format("DD-MM-YYYY HH:mm") : "-";

  const formatReconWindow = (startValue, endValue, fallbackValue = "-") => {
    const startText = formatDateTimeDisplay(startValue);
    const endText = formatDateTimeDisplay(endValue);
    if (startText === "-" && endText === "-") return fallbackValue;
    return `${startText} to ${endText}`;
  };

  // =====================================================================
  // SHARED DATE RANGE
  // =====================================================================
  const [startDate, setStartDate] = useState(
    moment().startOf("month").format("YYYY-MM-DDTHH:mm")
  );
  const [endDate, setEndDate] = useState(
    moment().endOf("month").format("YYYY-MM-DDTHH:mm")
  );

  // =====================================================================
  // LUBE TAB DATE-TIME RANGE
  // =====================================================================
  const [lubeStartDateTime, setLubeStartDateTime] = useState(
    moment().startOf("day").format("YYYY-MM-DDTHH:mm")
  );
  const [lubeEndDateTime, setLubeEndDateTime] = useState(
    moment().endOf("day").format("YYYY-MM-DDTHH:mm")
  );

  // =====================================================================
  // NOZZLE REPORT STATE
  // =====================================================================
  const [fuelType, setFuelType] = useState("ALL");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [nozzleTotals, setNozzleTotals] = useState({
    gross_volume: 0,
    testing_volume: 0,
    actual_volume: 0,
    gross_amount: 0,
    testing_amount: 0,
    actual_amount: 0,
  });

  // Header reconciliation totals (Nozzle tab)
  const [reconciliationTotals, setReconciliationTotals] = useState({
    total_cash_amount: 0,
    total_upi_amount: 0,
    total_card_amount: 0,
    total_hp_pay_amount: 0,
    total_credit_amount: 0,
    total_test_amount: 0,
    total_others_amount: 0,
    total_fleet_amount: 0,
    total_misc_amount: 0,
    employeecredit: 0,
  });

  const [showReconciliationCards, setShowReconciliationCards] = useState(true);

  // =====================================================================
  // P&L STATE
  // =====================================================================
  const [pnl, setPnl] = useState(null);
  const [pnlLoading, setPnlLoading] = useState(false);
  const [rangeType, setRangeType] = useState("custom");
  const [pnlNozzleData, setPnlNozzleData] = useState([]);

  // =====================================================================
  // LUBE REPORT STATE
  // =====================================================================
  const [lubeSales, setLubeSales] = useState([]);
  const [lubeSummary, setLubeSummary] = useState([]);
  const [lubeTotals, setLubeTotals] = useState({
    total_quantity: 0,
    total_amount: 0,
  });
  const [lubeLoading, setLubeLoading] = useState(false);

  // =====================================================================
  // RECONCILIATION REPORT STATE
  // =====================================================================
  const [reconRows, setReconRows] = useState([]);
  const [reconLoading, setReconLoading] = useState(false);
  const [showReconCreditSplit, setShowReconCreditSplit] = useState(false);
  const [reconSplitLoading, setReconSplitLoading] = useState(false);
  const [selectedReconSplit, setSelectedReconSplit] = useState(null);

  // =====================================================================
  // CUSTOMER CREDIT REPORT STATE
  // =====================================================================
  const [custCreditRows, setCustCreditRows] = useState([]);
  const [custCreditLoading, setCustCreditLoading] = useState(false);
  const [attendanceRows, setAttendanceRows] = useState([]);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendanceShiftFilter, setAttendanceShiftFilter] = useState("ALL");
  const [attendancePreset, setAttendancePreset] = useState("month");
  const [attendanceSearch, setAttendanceSearch] = useState("");
  const [attendanceSummary, setAttendanceSummary] = useState({
    totalEmployees: 0,
    eligibleEmployeeDays: 0,
    presentEmployeeDays: 0,
    absentEmployeeDays: 0,
    grossPayable: 0,
    creditAdded: 0,
    creditRecovered: 0,
    creditDeduction: 0,
    runningCreditBalance: 0,
    netPayable: 0,
    shiftCounts: [],
  });
  const [attendancePeriod, setAttendancePeriod] = useState(null);
  const [selectedAttendanceEmployee, setSelectedAttendanceEmployee] = useState(null);
  const [showAttendanceDetail, setShowAttendanceDetail] = useState(false);

  const [ccCustomerId, setCcCustomerId] = useState("ALL");
  const [ccSearch, setCcSearch] = useState("");
  const [tableSorts, setTableSorts] = useState({
    nozzle: { key: null, direction: "asc" },
    lube: { key: null, direction: "asc" },
    recon: { key: null, direction: "asc" },
    custcredit: { key: null, direction: "asc" },
    attendance: { key: null, direction: "asc" },
  });

  // =====================================================================
  // PAGINATION: NOZZLE TAB
  // =====================================================================
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;
  const offset = currentPage * itemsPerPage;
  const sortedData = useMemo(
    () => sortRowsByConfig(data, tableSorts.nozzle),
    [data, tableSorts.nozzle]
  );
  const currentData = sortedData.slice(offset, offset + itemsPerPage);
  const pageCount = Math.ceil(sortedData.length / itemsPerPage) || 0;

  // =====================================================================
  // PAGINATION: LUBE TAB
  // =====================================================================
  const [lubePage, setLubePage] = useState(0);
  const lubeItemsPerPage = 10;
  const lubeOffset = lubePage * lubeItemsPerPage;
  const sortedLubeSales = useMemo(
    () => sortRowsByConfig(lubeSales, tableSorts.lube),
    [lubeSales, tableSorts.lube]
  );
  const currentLubeSales = sortedLubeSales.slice(
    lubeOffset,
    lubeOffset + lubeItemsPerPage
  );
  const lubePageCount = Math.ceil(sortedLubeSales.length / lubeItemsPerPage) || 0;

  // =====================================================================
  // PAGINATION: RECON TAB
  // =====================================================================
  const [reconPage, setReconPage] = useState(0);
  const reconItemsPerPage = 5;
  const reconOffset = reconPage * reconItemsPerPage;
  const sortedReconRows = useMemo(
    () => sortRowsByConfig(reconRows, tableSorts.recon),
    [reconRows, tableSorts.recon]
  );
  const currentReconRows = sortedReconRows.slice(
    reconOffset,
    reconOffset + reconItemsPerPage
  );
  const reconPageCount = Math.ceil(sortedReconRows.length / reconItemsPerPage) || 0;

  useEffect(() => {
    if (!canViewPnl && activeTab === "pnl") {
      setActiveTab("nozzle");
    }
  }, [activeTab, canViewPnl]);

  // =====================================================================
  // PAGINATION: CUSTOMER CREDIT TAB
  // =====================================================================
  const [ccPage, setCcPage] = useState(0);
  const ccItemsPerPage = 5;
  const ccOffset = ccPage * ccItemsPerPage;
  const [attendancePage, setAttendancePage] = useState(0);
  const attendanceItemsPerPage = 8;

  const money = (value) =>
    Number(value || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const stripHtml = (value) =>
    String(value ?? "")
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  function getSortValue(row, key) {
    if (!row) return "";
    if (key === "dateTime") {
      return row.closing_reading_datetime || row.sale_date || row.recon_end_datetime || row.recon_start_datetime || row.credit_created_at || row.created_at || "";
    }
    if (key === "creditCreated") return row.credit_created_at || row.created_at || "";
    if (key === "latestPaymentDate") return row.latest_payment_date || "";
    if (key === "reconStart") return row.recon_start_datetime || row.recon_date || row.created_at || "";
    if (key === "reconEnd") return row.recon_end_datetime || row.recon_date || row.created_at || "";
    const value = row[key];
    if (value === null || value === undefined) return "";
    return value;
  }

  function compareSortValues(a, b) {
    const numA = Number(a);
    const numB = Number(b);
    const aIsNum = a !== "" && a !== null && a !== undefined && Number.isFinite(numA);
    const bIsNum = b !== "" && b !== null && b !== undefined && Number.isFinite(numB);
    if (aIsNum && bIsNum) return numA - numB;

    const dateA = moment(a, moment.ISO_8601, true);
    const dateB = moment(b, moment.ISO_8601, true);
    if (dateA.isValid() && dateB.isValid()) return dateA.valueOf() - dateB.valueOf();

    return String(a ?? "").localeCompare(String(b ?? ""), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  }

  function sortRowsByConfig(rows = [], config = {}) {
    const { key, direction } = config || {};
    if (!key) return rows;
    const dir = direction === "desc" ? -1 : 1;
    return [...rows].sort((a, b) => {
      const result = compareSortValues(getSortValue(a, key), getSortValue(b, key));
      return result * dir;
    });
  }

  function handleTableSort(table, key) {
    setTableSorts((prev) => {
      const current = prev?.[table] || { key: null, direction: "asc" };
      const nextDirection =
        current.key === key && current.direction === "asc" ? "desc" : "asc";
      return {
        ...prev,
        [table]: { key, direction: nextDirection },
      };
    });
    if (table === "nozzle") setCurrentPage(0);
    if (table === "lube") setLubePage(0);
    if (table === "recon") setReconPage(0);
    if (table === "custcredit") setCcPage(0);
    if (table === "attendance") setAttendancePage(0);
  }

  function sortIndicator(table, key) {
    const current = tableSorts?.[table];
    if (!current || current.key !== key) return "";
    return current.direction === "asc" ? " ↑" : " ↓";
  }

  const buildCreditSplitRows = (rows = []) => {
    const map = {};

    rows
      .filter((row) => Number(row?.amount || 0) > 0)
      .forEach((row) => {
        const name = row.customer_name || `Customer #${row.customer_id || "-"}`;
        if (!map[name]) {
          map[name] = {
            name,
            amount: 0,
            volume: 0,
            fuelTypes: new Set(),
            comments: new Set(),
            entries: 0,
          };
        }

        map[name].amount += Number(row.amount || 0);
        map[name].volume += Number(row.volume || 0);
        map[name].entries += 1;
        if (row.fuel_type) map[name].fuelTypes.add(row.fuel_type);
        if (row.credit_comment) map[name].comments.add(stripHtml(row.credit_comment));
      });

    return Object.values(map)
      .map((row, idx) => ({
        name: row.name,
        amount: Number(row.amount || 0),
        volume: Number(row.volume || 0),
        entries: Number(row.entries || 0),
        fuelType: Array.from(row.fuelTypes).filter(Boolean).join(", ") || "-",
        comment: Array.from(row.comments).filter(Boolean).join(" | ") || "-",
        fill: reportChartColors[idx % reportChartColors.length],
      }))
      .sort((a, b) => b.amount - a.amount);
  };

  const buildFuelSplitRows = (rows = []) =>
    (rows || [])
      .map((row, idx) => ({
        fuelType: row.fuel_type || "Unknown",
        count: Number(row.row_count || 0),
        volume: Number(row.volume || 0),
        testingVolume: Number(row.testing_volume || 0),
        amount: Number(row.amount || 0),
        testingAmount: Number(row.testing_amount || 0),
        fill: reportChartColors[idx % reportChartColors.length],
      }))
      .sort((a, b) => b.amount - a.amount);

  const buildSettlementSplitRows = (recon = null) => {
    if (!recon) return [];
    return [
      { label: "Cash", value: Number(recon.cash_amount || 0) },
      { label: "UPI", value: Number(recon.upi_amount || 0) },
      { label: "Card", value: Number(recon.card_amount || 0) },
      { label: "HP Pay", value: Number(recon.hp_pay_amount || 0) },
      { label: "Credit", value: Number(recon.credit_amount || 0) },
      { label: "Test", value: Number(recon.test_amount || 0) },
      { label: "Others", value: Number(recon.others_amount || 0) },
      { label: "Fleet", value: Number(recon.fleet || 0) },
      { label: "Misc", value: Number(recon.misc || 0) },
      { label: "Remaining", value: Number(recon.remaining_balance || 0) },
    ].filter((row) => Number(row.value || 0) > 0);
  };

  const buildExcelHeaderRows = (title, subtitle, lines = []) => [
    [companyName],
    [bunkName],
    [bunkAddress],
    [`Admin Phone: ${adminPhone}`],
    [title],
    [subtitle],
    [`Generated On: ${moment().format("DD-MM-YYYY hh:mm A")}`],
    ...lines.map((line) => [line]),
    [],
  ];

  const applyExcelHeader = (ws, totalCols) => {
    const lastCol = Math.max(0, totalCols - 1);
    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: lastCol } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: lastCol } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: lastCol } },
      { s: { r: 3, c: 0 }, e: { r: 3, c: lastCol } },
      { s: { r: 4, c: 0 }, e: { r: 4, c: lastCol } },
      { s: { r: 5, c: 0 }, e: { r: 5, c: lastCol } },
      { s: { r: 6, c: 0 }, e: { r: 6, c: lastCol } },
    ];
  };

  const assetToDataUrl = async (assetUrl) => {
    try {
      const res = await fetch(assetUrl);
      const blob = await res.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      return null;
    }
  };

  const toExcelBase64 = (dataUrl) =>
    typeof dataUrl === "string" && dataUrl.includes(",")
      ? dataUrl.split(",")[1]
      : dataUrl;

  const addPdfHeader = async (doc, title, subtitle, options = {}) => {
    const [fuelLogo, companyLogo] = await Promise.all([
      assetToDataUrl(FuelFlowLogo),
      assetToDataUrl(activeCompanyLogo),
    ]);
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFillColor(15, 23, 42);
    doc.rect(0, 0, pageWidth, 34, "F");
    if (fuelLogo) doc.addImage(fuelLogo, "PNG", 12, 7, 18, 18);
    if (companyLogo) doc.addImage(companyLogo, "PNG", pageWidth - 30, 7, 18, 18);

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text(companyName, 34, 13);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(bunkName, 34, 19);
    doc.text(`${bunkAddress} | ${adminPhone}`, 34, 25);
    doc.text(title, 34, 31);

    if (subtitle) {
      doc.setTextColor(55, 65, 81);
      doc.setFontSize(10);
      doc.text(subtitle, 14, 41);
    }

    if (options.badgeText) {
      doc.setFillColor(37, 99, 235);
      doc.roundedRect(pageWidth - 62, 37, 48, 8, 2, 2, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8);
      doc.text(options.badgeText, pageWidth - 58, 42.5);
    }
  };

  const saveBrandedPdf = async ({
    title,
    subtitle,
    filename,
    head,
    body,
    orientation = "landscape",
    badgeText,
    startY = 47,
  }) => {
    const doc = new jsPDF({ orientation, unit: "mm", format: "a4" });
    await addPdfHeader(doc, title, subtitle, { badgeText });
    autoTable(doc, {
      startY,
      head,
      body,
      styles: {
        fontSize: 8,
        cellPadding: 2.2,
        lineColor: [15, 23, 42],
        lineWidth: 0.35,
      },
      headStyles: {
        fillColor: [15, 23, 42],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        lineColor: [15, 23, 42],
        lineWidth: 0.45,
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252],
      },
      tableLineColor: [15, 23, 42],
      tableLineWidth: 0.5,
      margin: { top: startY, left: 10, right: 10 },
    });
    doc.save(filename);
  };

  const saveExcelWithBranding = async ({
    filename,
    sheetName,
    title,
    subtitle,
    rows,
    columns,
    columnWidths,
  }) => {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = "FuelFlow";
    workbook.created = new Date();

    const worksheet = workbook.addWorksheet(sheetName);
    const [fuelLogo, companyLogo] = await Promise.all([
      assetToDataUrl(FuelFlowLogo),
      assetToDataUrl(activeCompanyLogo),
    ]);

    worksheet.getRow(1).height = 24;
    worksheet.getRow(2).height = 24;
    worksheet.getRow(3).height = 18;

    const headerRows = [
      companyName,
      bunkName,
      bunkAddress,
      `Admin Phone: ${adminPhone}`,
      title,
      subtitle,
      `Generated On: ${moment().format("DD-MM-YYYY hh:mm A")}`,
    ];

    headerRows.forEach((line, index) => {
      const row = worksheet.getRow(index + 3);
      row.getCell(1).value = line;
      worksheet.mergeCells(index + 3, 1, index + 3, Math.max(1, columns.length));
      const cell = row.getCell(1);
      cell.alignment = { horizontal: "left", vertical: "middle" };
      cell.font = {
        bold: index <= 4,
        size: index === 4 ? 14 : index <= 3 ? 12 : 10,
        color: { argb: "FF0F172A" },
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: index <= 3 ? "FFF8FAFC" : "FFFFFFFF" },
      };
    });

    worksheet.addRow([]);
    const headerRow = worksheet.addRow(columns);
    headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
    headerRow.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF0F172A" },
    };

    rows.forEach((row) => worksheet.addRow(row));
    worksheet.columns = columnWidths.map((width, idx) => ({
      key: `col_${idx + 1}`,
      width,
    }));

    if (fuelLogo) {
      const logoId = workbook.addImage({ base64: toExcelBase64(fuelLogo), extension: "png" });
      worksheet.addImage(logoId, "A1:B2");
    }
    if (companyLogo) {
      const logoId = workbook.addImage({ base64: toExcelBase64(companyLogo), extension: "png" });
      const logoCol = String.fromCharCode(64 + Math.max(1, columns.length));
      worksheet.addImage(logoId, `${logoCol}1:${logoCol}2`);
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const modernTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    return (
      <div
        style={{
          background: "rgba(255,255,255,0.98)",
          border: "1px solid rgba(15,23,42,0.12)",
          boxShadow: "0 16px 36px rgba(15,23,42,0.12)",
          borderRadius: 14,
          padding: "10px 12px",
          minWidth: 160,
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 900, color: "#0f172a", marginBottom: 8 }}>
          {label}
        </div>
        {payload.map((entry) => (
          <div
            key={entry.name}
            style={{ display: "flex", justifyContent: "space-between", gap: 14, marginTop: 4 }}
          >
            <span style={{ color: "#475569", fontSize: 12, fontWeight: 700 }}>{entry.name}</span>
            <strong style={{ color: entry.color || "#0f172a", fontSize: 12 }}>
              {typeof entry.value === "number"
                ? money(entry.value)
                : entry.value}
            </strong>
          </div>
        ))}
      </div>
    );
  };

  // =====================================================================
  // API: NOZZLE REPORT FETCH
  // =====================================================================
  const fetchReport = async () => {
    setLoading(true);
    try {
      const startDT = moment(startDate).format("YYYY-MM-DD HH:mm:ss");
      const endDT = moment(endDate).format("YYYY-MM-DD HH:mm:ss");
      const res = await api.get("/api/nozzle-readings/full", {
        params: { startDate: startDT, endDate: endDT, fuelType },
        headers: { Authorization: `Bearer ${token}` },
      });

      const rows = res.data.data || [];
      const rt = res.data.reconciliationTotals || {};
      const totals = res.data.totals || {};

      setData(rows);

      setNozzleTotals({
        gross_volume: Number(totals.gross_volume || 0),
        testing_volume: Number(totals.testing_volume || 0),
        actual_volume: Number(totals.actual_volume || 0),
        gross_amount: Number(totals.gross_amount || 0),
        testing_amount: Number(totals.testing_amount || 0),
        actual_amount: Number(totals.actual_amount || 0),
      });

      setReconciliationTotals({
        total_cash_amount: rt.total_cash_amount || 0,
        total_upi_amount: rt.total_upi_amount || 0,
        total_card_amount: rt.total_card_amount || 0,
        total_hp_pay_amount: rt.total_hp_pay_amount || 0,
        total_credit_amount: rt.total_credit_amount || 0,
        total_test_amount: rt.total_test_amount || 0,
        total_others_amount: rt.total_others_amount || 0,
        total_fleet_amount: rt.total_fleet_amount || 0,
        total_misc_amount: rt.total_misc_amount || 0,
        employeecredit: rt.employeecredit || 0,
      });

      setCurrentPage(0);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  // =====================================================================
  // API: RECONCILIATION REPORT FETCH
  // =====================================================================
  const fetchReconReport = async () => {
    setReconLoading(true);
    try {
      const startDT = moment(startDate).format("YYYY-MM-DD HH:mm:ss");
      const endDT = moment(endDate).format("YYYY-MM-DD HH:mm:ss");
      const res = await api.get("/api/reconciliation", {
        params: {
          startDateTime: startDT,
          endDateTime: endDT,
          startDate: startDT,
          endDate: endDT,
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      const rows = Array.isArray(res.data) ? res.data : res.data.data || [];
      setReconRows(rows);
      setReconPage(0);
      await fetchCustomerCreditReport();
    } catch (err) {
      console.error("Error fetching reconciliation report:", err);
    }
    setReconLoading(false);
  };

  const openPeriodReconSplit = () => {
    setSelectedReconSplit(null);
    setReconSplitLoading(false);
    setShowReconCreditSplit(true);
  };

  const openReconRowSplit = async (row, source = "credit") => {
    if (!row?.id) return;
    setShowReconCreditSplit(true);
    setReconSplitLoading(true);
    setSelectedReconSplit({
      id: row.id,
      source,
      recon_date: row.recon_date || "-",
      recon_start_datetime: row.recon_start_datetime || row.recon_date || "-",
      recon_end_datetime: row.recon_end_datetime || row.recon_date || "-",
      employee_name: row.employee_name || "-",
      shift: row.shift || "-",
      total_amount: Number(row.total_amount || 0),
      credit_amount: Number(row.credit_amount || 0),
      rows: [],
    });

    try {
      const res = await api.get(`/api/reconciliation/${row.id}/split`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const rows = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setSelectedReconSplit((prev) => ({
        ...(prev || {}),
        rows,
        reconciliation: res.data?.reconciliation || prev?.reconciliation || null,
        fuelRows: Array.isArray(res.data?.fuel_split)
          ? res.data.fuel_split
          : prev?.fuelRows || [],
      }));
    } catch (err) {
      console.error("Error fetching reconciliation split:", err);
      setSelectedReconSplit((prev) => ({
        ...(prev || {}),
        rows: [],
        fuelRows: [],
      }));
    } finally {
      setReconSplitLoading(false);
    }
  };

  // =====================================================================
  // API: CUSTOMER CREDIT REPORT FETCH
  // =====================================================================
  const fetchCustomerCreditReport = async () => {
    setCustCreditLoading(true);
    try {
      const startDT = moment(startDate).format("YYYY-MM-DD HH:mm:ss");
      const endDT = moment(endDate).format("YYYY-MM-DD HH:mm:ss");
      const res = await api.get("/api/users/customercreditreport", {
        params: { startDateTime: startDT, endDateTime: endDT },
        headers: { Authorization: `Bearer ${token}` },
      });
      const rows = Array.isArray(res.data) ? res.data : res.data.data || [];
      setCustCreditRows(rows);
      setCcPage(0);
    } catch (err) {
      console.error("Error fetching customer credit report:", err);
    }
    setCustCreditLoading(false);
  };

  const fetchAttendanceReport = async ({
    customStart = startDate,
    customEnd = endDate,
    customShift = attendanceShiftFilter,
  } = {}) => {
    setAttendanceLoading(true);
    try {
      const startDT = moment(customStart).format("YYYY-MM-DD HH:mm:ss");
      const endDT = moment(customEnd).format("YYYY-MM-DD HH:mm:ss");
      const res = await api.get("/api/reports/attendance", {
        params: {
          startDateTime: startDT,
          endDateTime: endDT,
          shift: customShift || "ALL",
        },
        headers: { Authorization: `Bearer ${token}` },
      });

      setAttendanceRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
      setAttendanceSummary(
        res.data?.summary || {
          totalEmployees: 0,
          eligibleEmployeeDays: 0,
          presentEmployeeDays: 0,
          absentEmployeeDays: 0,
          grossPayable: 0,
          creditAdded: 0,
          creditRecovered: 0,
          creditDeduction: 0,
          runningCreditBalance: 0,
          netPayable: 0,
          shiftCounts: [],
        }
      );
      setAttendancePeriod(res.data?.period || null);
      setAttendancePage(0);
    } catch (err) {
      console.error("Error fetching attendance report:", err);
    }
    setAttendanceLoading(false);
  };

  useEffect(() => {
    fetchReport();
    fetchCustomerCreditReport();
    // eslint-disable-next-line
  }, []);

  const applyAttendancePreset = (preset) => {
    setAttendancePreset(preset);
    const today = moment().endOf("day");
    let start = today.clone().startOf("month");

    if (preset === "week") {
      start = today.clone().startOf("isoWeek");
    } else if (preset === "15days") {
      start = today.clone().subtract(14, "days").startOf("day");
    }

    const nextStart = start.format("YYYY-MM-DDTHH:mm");
    const nextEnd = today.format("YYYY-MM-DDTHH:mm");
    setStartDate(nextStart);
    setEndDate(nextEnd);
    fetchAttendanceReport({
      customStart: nextStart,
      customEnd: nextEnd,
      customShift: attendanceShiftFilter,
    });
  };

  // =====================================================================
  // NOZZLE TAB: OVERALL TOTALS
  // =====================================================================
  const totalGrossVolume = nozzleTotals.gross_volume;
  const totalTestingVolume = nozzleTotals.testing_volume;
  const totalActualVolume = nozzleTotals.actual_volume;
  const totalGrossAmount = nozzleTotals.gross_amount;
  const totalTestingAmount = nozzleTotals.testing_amount;
  const totalActualAmount = nozzleTotals.actual_amount;

  // totals by fuel type
  const totalsByFuel = data.reduce((acc, d) => {
    const ft = d.fuel_type || "Unknown";
    if (!acc[ft]) {
      acc[ft] = {
        gross_volume: 0,
        testing_volume: 0,
        actual_volume: 0,
        gross_amount: 0,
        testing_amount: 0,
        actual_amount: 0,
      };
    }

    acc[ft].gross_volume += parseFloat(d.dispensed_volume) || 0;
    acc[ft].testing_volume += parseFloat(d.testing_liters) || 0;
    acc[ft].actual_volume +=
      parseFloat(
        d.actual_dispensed_volume != null
          ? d.actual_dispensed_volume
          : d.dispensed_volume || 0
      ) || 0;

    acc[ft].gross_amount += parseFloat(d.amount) || 0;
    acc[ft].testing_amount += parseFloat(d.testing_amount) || 0;
    acc[ft].actual_amount +=
      parseFloat(
        d.actual_amount != null
          ? d.actual_amount
          : d.amount || 0
      ) || 0;

    return acc;
  }, {});

  // =====================================================================
  // EXPORT: NOZZLE REPORT TO EXCEL
  // =====================================================================
  const exportNozzleToExcel = async () => {
    if (!data || data.length === 0) return;

    const columns = [
      "Nozzle",
      "Fuel Type",
      "Pump",
      "Opening",
      "Closing",
      "Gross Volume (L)",
      "Testing Volume (L)",
      "Actual Sale Volume (L)",
      "Gross Amount (₹)",
      "Testing Amount (₹)",
      "Actual Sale Amount (₹)",
      "Reading Datetime",
    ];
    const rows = data.map((r) => [
      r.nozzle_name,
      r.fuel_type,
      r.pump_name || "—",
      r.opening_reading,
      r.closing_reading,
      Number(r.dispensed_volume || 0).toFixed(3),
      Number(r.testing_liters || 0).toFixed(3),
      Number(
        r.actual_dispensed_volume != null
          ? r.actual_dispensed_volume
          : r.dispensed_volume || 0
      ).toFixed(3),
      Number(r.amount || 0).toFixed(2),
      Number(r.testing_amount || 0).toFixed(2),
      Number(r.actual_amount != null ? r.actual_amount : r.amount || 0).toFixed(2),
      r.closing_reading_datetime || "-",
    ]);

    await saveExcelWithBranding({
      filename: `NozzleReport_${startDate}_${endDate}.xlsx`,
      sheetName: "NozzleReport",
      title: "Nozzle Report",
      subtitle: `Period: ${formatDateTimeDisplay(startDate)} to ${formatDateTimeDisplay(endDate)} | Fuel filter: ${fuelType === "ALL" ? "All" : fuelType}`,
      rows,
      columns,
      columnWidths: [18, 14, 18, 12, 12, 18, 18, 18, 18, 18, 18, 22],
    });
  };

  const exportNozzleToPDF = async () => {
    if (!data || data.length === 0) return;

    const body = data.map((r) => [
      r.nozzle_name || "-",
      r.fuel_type || "-",
      r.pump_name || "—",
      Number(r.opening_reading || 0).toFixed(3),
      Number(r.closing_reading || 0).toFixed(3),
      Number(r.dispensed_volume || 0).toFixed(3),
      Number(r.testing_liters || 0).toFixed(3),
      Number(
        r.actual_dispensed_volume != null ? r.actual_dispensed_volume : r.dispensed_volume || 0
      ).toFixed(3),
      Number(r.amount || 0).toFixed(2),
      Number(r.testing_amount || 0).toFixed(2),
      Number(r.actual_amount != null ? r.actual_amount : r.amount || 0).toFixed(2),
      r.closing_reading_datetime || "-",
    ]);

    await saveBrandedPdf({
      title: "Nozzle Report",
      subtitle: `Period: ${formatDateTimeDisplay(startDate)} to ${formatDateTimeDisplay(endDate)} | Fuel filter: ${fuelType === "ALL" ? "All" : fuelType}`,
      filename: `NozzleReport_${startDate}_${endDate}.pdf`,
      badgeText: "NOZZLE",
      head: [[
        "Nozzle",
        "Fuel Type",
        "Pump",
        "Opening",
        "Closing",
        "Gross Volume",
        "Testing Volume",
        "Actual Volume",
        "Gross Amount",
        "Testing Amount",
        "Actual Amount",
        "Reading Datetime",
      ]],
      body,
      orientation: "landscape",
      startY: 47,
    });
  };

  // =====================================================================
  // PAGINATION HANDLERS
  // =====================================================================
  const handlePageClick = (e) => setCurrentPage(e.selected);
  const handleLubePageClick = (e) => setLubePage(e.selected);
  const handleReconPageClick = (e) => setReconPage(e.selected);
  const handleCcPageClick = (e) => setCcPage(e.selected);
  const handleAttendancePageClick = (e) => setAttendancePage(e.selected);

  // =====================================================================
  // API: LUBE REPORT FETCH
  // =====================================================================
  const fetchLubeReport = async () => {
    setLubeLoading(true);
    try {
      const startDT = moment(lubeStartDateTime).format("YYYY-MM-DD HH:mm:ss");
      const endDT = moment(lubeEndDateTime).format("YYYY-MM-DD HH:mm:ss");

      const res = await api.get("/api/lube-sales/report", {
        params: { startDateTime: startDT, endDateTime: endDT },
        headers: { Authorization: `Bearer ${token}` },
      });

      setLubeSales(res.data.sales || []);
      setLubeSummary(res.data.summaryByProduct || []);
      setLubeTotals(res.data.totals || { total_quantity: 0, total_amount: 0 });
      setLubePage(0);
    } catch (err) {
      console.error("Error fetching Lube report:", err);
    }
    setLubeLoading(false);
  };

  // =====================================================================
  // P&L FETCH + HELPERS
  // =====================================================================
  const fetchPnlNozzleSeries = async (s, e) => {
    try {
      const res = await api.get("/api/nozzle-readings/full", {
        params: { startDate: s, endDate: e, fuelType: "ALL" },
        headers: { Authorization: `Bearer ${token}` },
      });
      setPnlNozzleData(res.data.data || []);
    } catch (err) {
      console.error("Error fetching P&L nozzle series:", err);
    }
  };

  const fetchPnL = async (customStart, customEnd) => {
    const s = customStart || startDate;
    const e = customEnd || endDate;
    const startDT = moment(s).format("YYYY-MM-DD HH:mm:ss");
    const endDT = moment(e).format("YYYY-MM-DD HH:mm:ss");

    setPnlLoading(true);
    try {
      const res = await api.get("/api/reports/pnl", {
        params: { startDate: startDT, endDate: endDT },
        headers: { Authorization: `Bearer ${token}` },
      });
      setPnl(res.data.pnl || null);
      await fetchPnlNozzleSeries(startDT, endDT);
    } catch (err) {
      console.error("Error fetching P&L:", err);
    }
    setPnlLoading(false);
  };

  const applyRange = (type) => {
    setRangeType(type);
    const today = moment();

    let s;
    let e;

    if (type === "week") {
      e = today.clone();
      s = today.clone().subtract(6, "days");
    } else if (type === "month") {
      s = today.clone().startOf("month");
      e = today.clone().endOf("month");
    } else if (type === "quarter") {
      s = today.clone().startOf("quarter");
      e = today.clone().endOf("quarter");
    } else if (type === "year") {
      s = today.clone().startOf("year");
      e = today.clone().endOf("year");
    } else {
      s = moment(startDate);
      e = moment(endDate);
    }

    const sStr = s.format("YYYY-MM-DDTHH:mm");
    const eStr = e.format("YYYY-MM-DDTHH:mm");

    setStartDate(sStr);
    setEndDate(eStr);

    fetchPnL(sStr, eStr);
  };

  const buildRevenueSeries = () => {
    if (!pnlNozzleData || pnlNozzleData.length === 0) return [];

    const start = moment(startDate);
    const end = moment(endDate);
    const diffDays = end.diff(start, "days") + 1;

    let mode;
    if (rangeType === "week") mode = "day";
    else if (rangeType === "month") mode = "week";
    else if (rangeType === "quarter" || rangeType === "year") mode = "month";
    else mode = diffDays <= 30 ? "day" : "month";

    const bucketMap = {};

    pnlNozzleData.forEach((r) => {
      if (!r.closing_reading_datetime) return;
      const m = moment(r.closing_reading_datetime);
      let key;
      let label;

      if (mode === "day") {
        key = m.format("YYYY-MM-DD");
        label = m.format("DD MMM");
      } else if (mode === "week") {
        key = m.format("YYYY-[W]WW");
        label = "Wk " + m.format("WW");
      } else {
        key = m.format("YYYY-MM");
        label = m.format("MMM YYYY");
      }

      if (!bucketMap[key]) bucketMap[key] = { key, label, revenue: 0 };
      bucketMap[key].revenue +=
        parseFloat(r.actual_amount != null ? r.actual_amount : r.amount) || 0;
    });

    return Object.values(bucketMap).sort((a, b) => (a.key > b.key ? 1 : -1));
  };

  const revenueSeries = buildRevenueSeries();

  const revenueBreakdown = pnl
  ? [
      { name: "Fuel", revenue: Number(pnl.gross_fuel_revenue || 0) },
      { name: "Lubes", revenue: Number(pnl.gross_lube_revenue || 0) },
      { name: "Discount", revenue: -Number(pnl.total_discount || 0) },
    ]
  : [];

  const expenseBreakdown = pnl
    ? [
        { name: "Salary", value: Number(pnl.salary_expense || 0) },
        { name: "Other", value: Number(pnl.other_operating_expenses || 0) },
        { name: "Discount", value: Number(pnl.total_discount || 0) },
      ]
    : [];

  const PIE_COLORS = ["#8884d8", "#82ca9d", "#ffc658", "#ff7f7f", "#8dd1e1"];

  const projectedRevenue = (() => {
  if (!pnl) return 0;
  const start = moment(startDate);
  const end = moment(endDate);
  const diffDays = Math.max(1, end.diff(start, "days") + 1);
  const perDay = Number((pnl.net_revenue ?? pnl.total_revenue) || 0) / diffDays;
  return perDay * 30;
})();

  const exportPnlToExcel = async () => {
    if (!pnl) return;

    const columns = ["Metric", "Value"];
    const rows = [
      ["Total Fuel Revenue", money(pnl.gross_fuel_revenue || 0)],
      ["Total Lube Revenue", money(pnl.gross_lube_revenue || 0)],
      ["Total Revenue", money(pnl.gross_total_revenue || 0)],
      ["Net Revenue", money(pnl.net_total_revenue || 0)],
      ["Discount", money(pnl.total_discount || 0)],
      ["Fuel Gross Margin", money(pnl.fuel_gross_margin || 0)],
      ["Lube Gross Margin", money(pnl.lube_gross_margin || 0)],
      ["Total Gross Margin", money(pnl.total_gross_margin || 0)],
      ["Salary Expense", money(pnl.salary_expense || 0)],
      ["Other Operating Expenses", money(pnl.other_operating_expenses || 0)],
      ["Total Operating Expenses", money(pnl.total_operating_expenses || 0)],
      ["Operating Profit", money(pnl.operating_profit || 0)],
      ["Net Profit", money(pnl.net_profit || 0)],
    ];

    await saveExcelWithBranding({
      filename: `PnL_${startDate}_${endDate}.xlsx`,
      sheetName: "PnL",
      title: "Profit & Loss Dashboard",
      subtitle: `Period: ${formatDateTimeDisplay(startDate)} to ${formatDateTimeDisplay(endDate)}`,
      rows,
      columns,
      columnWidths: [34, 18],
    });
  };

  const exportPnlToPDF = async () => {
    if (!pnl) return;

    const body = [
      ["Total Fuel Revenue", money(pnl.gross_fuel_revenue || 0)],
      ["Total Lube Revenue", money(pnl.gross_lube_revenue || 0)],
      ["Total Revenue", money(pnl.gross_total_revenue || 0)],
      ["Net Revenue", money(pnl.net_total_revenue || 0)],
      ["Discount", money(pnl.total_discount || 0)],
      ["Fuel Gross Margin", (pnl.fuel_gross_margin || 0).toFixed(2)],
      ["Lube Gross Margin", (pnl.lube_gross_margin || 0).toFixed(2)],
      ["Total Gross Margin", (pnl.total_gross_margin || 0).toFixed(2)],
      ["Salary Expense", (pnl.salary_expense || 0).toFixed(2)],
      ["Other Operating Expenses", (pnl.other_operating_expenses || 0).toFixed(2)],
      ["Total Operating Expenses", (pnl.total_operating_expenses || 0).toFixed(2)],
      ["Operating Profit", (pnl.operating_profit || 0).toFixed(2)],
      ["Net Profit", (pnl.net_profit || 0).toFixed(2)],
    ];

    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    await addPdfHeader(doc, "Profit & Loss Dashboard", `Period: ${formatDateTimeDisplay(startDate)} to ${formatDateTimeDisplay(endDate)}`, {
      badgeText: "P&L",
    });
    doc.autoTable({
      startY: 47,
      head: [["Metric", "Value"]],
      body,
      margin: { top: 47, left: 12, right: 12 },
      styles: { fontSize: 9, cellPadding: 2, lineColor: [15, 23, 42], lineWidth: 0.35 },
      headStyles: { fillColor: [15, 23, 42], textColor: [255, 255, 255], lineColor: [15, 23, 42], lineWidth: 0.45 },
      tableLineColor: [15, 23, 42],
      tableLineWidth: 0.5,
    });

    doc.save(`PnL_${startDate}_${endDate}.pdf`);
  };

  const profitMarginPct = pnl
  ? Number(
      (pnl.net_total_revenue || 0)
        ? (Number(pnl.net_profit || 0) / Number(pnl.net_total_revenue || 0)) * 100
        : 0
    ).toFixed(2)
  : "0.00";

  const reconciliationCardConfig = [
    { key: "total_cash_amount", label: "Cash" },
    { key: "total_upi_amount", label: "UPI" },
    { key: "total_card_amount", label: "Card" },
    { key: "total_hp_pay_amount", label: "HP Pay" },
    { key: "total_credit_amount", label: "Credit" },
    { key: "total_test_amount", label: "Test" },
    { key: "total_others_amount", label: "Others" },
    { key: "total_fleet_amount", label: "Fleet" },
    { key: "total_misc_amount", label: "Misc" },
    { key: "employeecredit", label: "Employee Credit" },
  ];

  const exportLubeToExcel = async () => {
    if (!lubeSales || lubeSales.length === 0) return;
    const columns = [
      "Sale Date",
      "Shift",
      "Operator",
      "Product",
      "Brand",
      "Unit",
      "Unit Price",
      "Qty",
      "Total Amount",
      "Comment",
    ];
    const rows = lubeSales.map((r) => [
      r.sale_date,
      r.shift,
      r.operator_name || r.operator_id || "",
      r.product_name,
      r.brand,
      r.unit,
      money(r.unit_price),
      money(r.quantity),
      money(r.total_amount),
      r.comment || "",
    ]);

    await saveExcelWithBranding({
      filename: `LubeReport_${moment(lubeStartDateTime).format("YYYYMMDD_HHmm")}_${moment(
        lubeEndDateTime
      ).format("YYYYMMDD_HHmm")}.xlsx`,
      sheetName: "LubeSales",
      title: "Lube Sales Report",
      subtitle: `Period: ${moment(lubeStartDateTime).format("DD-MM-YYYY HH:mm")} to ${moment(lubeEndDateTime).format("DD-MM-YYYY HH:mm")}`,
      rows,
      columns,
      columnWidths: [14, 10, 18, 20, 14, 10, 12, 12, 16, 24],
    });
  };

  const exportLubeToPDF = async () => {
    if (!lubeSales || lubeSales.length === 0) return;

    const body = lubeSales.map((r) => [
      r.sale_date || "-",
      r.shift || "-",
      r.operator_name || r.operator_id || "-",
      r.product_name || "-",
      r.brand || "-",
      r.unit || "-",
      money(r.unit_price || 0),
      money(r.quantity || 0),
      money(r.total_amount || 0),
      r.comment || "",
    ]);

    await saveBrandedPdf({
      title: "Lube Sales Report",
      subtitle: `Period: ${moment(lubeStartDateTime).format("DD-MM-YYYY HH:mm")} to ${moment(lubeEndDateTime).format("DD-MM-YYYY HH:mm")}`,
      filename: `LubeReport_${moment(lubeStartDateTime).format("YYYYMMDD_HHmm")}_${moment(
        lubeEndDateTime
      ).format("YYYYMMDD_HHmm")}.pdf`,
      badgeText: "LUBE",
      head: [[
        "Sale Date",
        "Shift",
        "Operator",
        "Product",
        "Brand",
        "Unit",
        "Unit Price",
        "Qty",
        "Total Amount",
        "Comment",
      ]],
      body,
      orientation: "landscape",
      startY: 47,
    });
  };

  // =====================================================================
  // RECON TAB: TOTALS + CHARTS
  // =====================================================================
  const reconTotals = useMemo(() => {
    const sum = (k) =>
      reconRows.reduce((acc, r) => acc + (Number(r?.[k]) || 0), 0);
    return {
      total_amount: sum("total_amount"),
      total_discount: sum("total_discount"),
      net_sale: sum("net_sale"),
      cash_amount: sum("cash_amount"),
      upi_amount: sum("upi_amount"),
      card_amount: sum("card_amount"),
      hp_pay_amount: sum("hp_pay_amount"),
      credit_amount: sum("credit_amount"),
      test_amount: sum("test_amount"),
      others_amount: sum("others_amount"),
      fleet: sum("fleet"),
      misc: sum("misc"),
      remaining_balance: sum("remaining_balance"),
    };
  }, [reconRows]);

  const reconPieData = useMemo(() => {
    return [
      { name: "Cash", value: reconTotals.cash_amount },
      { name: "UPI", value: reconTotals.upi_amount },
      { name: "Card", value: reconTotals.card_amount },
      { name: "HP Pay", value: reconTotals.hp_pay_amount },
      { name: "Credit", value: reconTotals.credit_amount },
      { name: "Test", value: reconTotals.test_amount },
      { name: "Others", value: reconTotals.others_amount },
      { name: "Fleet", value: reconTotals.fleet },
      { name: "Misc", value: reconTotals.misc },
    ].filter((x) => Number(x.value || 0) > 0);
  }, [reconTotals]);

  const reconByEmployee = useMemo(() => {
    const map = {};
    reconRows.forEach((r) => {
      const key = r.employee_name ?? "NA";
      if (!map[key]) map[key] = { employee_name: key, total: 0 };
      map[key].total += Number(r.total_amount || 0);
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [reconRows]);

  const exportReconToExcel = async () => {
    if (!reconRows || reconRows.length === 0) return;

    const columns = [
      "ID",
      "Start Time",
      "End Time",
      "Employee Name",
      "Shift",
      "Pump/Nozzle Summary",
      "Total Amount",
      "Missing Lube",
      "Total Discount",
      "Net Sale",
      "Cash",
      "UPI",
      "Card",
      "HP Pay",
      "Credit",
      "Test",
      "Others",
      "Fleet",
      "Misc",
      "Remaining Balance",
      "Comment",
      "Created At",
      "Updated At",
    ];
    const rows = reconRows.map((r) => [
      r.id,
      formatDateTimeDisplay(r.recon_start_datetime || r.recon_date),
      formatDateTimeDisplay(r.recon_end_datetime || r.recon_date),
      r.employee_name,
      r.shift,
      r.pump_nozzle_summary,
      money(r.total_amount),
      money(r.lube_adjustment),
      money(r.total_discount),
      money(r.net_sale),
      money(r.cash_amount),
      money(r.upi_amount),
      money(r.card_amount),
      money(r.hp_pay_amount),
      money(r.credit_amount),
      money(r.test_amount),
      money(r.others_amount),
      money(r.fleet),
      money(r.misc),
      money(r.remaining_balance),
      r.comment,
      r.created_at,
      r.updated_at,
    ]);

    await saveExcelWithBranding({
      filename: `Reconciliation_${startDate}_${endDate}.xlsx`,
      sheetName: "Reconciliation",
      title: "Reconciliation Report",
      subtitle: `Period: ${formatDateTimeDisplay(startDate)} to ${formatDateTimeDisplay(endDate)}`,
      rows,
      columns,
      columnWidths: [8, 16, 16, 12, 10, 18, 14, 14, 14, 12, 10, 10, 10, 10, 10, 10, 10, 14, 24, 16, 16],
    });
  };

  const exportReconToPDF = async () => {
    if (!reconRows || reconRows.length === 0) return;

    const body = reconRows.map((r) => [
      r.id,
      formatDateTimeDisplay(r.recon_start_datetime || r.recon_date),
      formatDateTimeDisplay(r.recon_end_datetime || r.recon_date),
      r.employee_name || "-",
      r.shift || "-",
      money(r.fuel_total || 0),
      money(r.lube_total || 0),
      money(r.lube_adjustment || 0),
      money(r.total_amount || 0),
      money(r.total_discount || 0),
      money(r.net_sale || 0),
      money(r.cash_amount || 0),
      money(r.upi_amount || 0),
      money(r.card_amount || 0),
      money(r.hp_pay_amount || 0),
      money(r.credit_amount || 0),
      money(r.test_amount || 0),
      money(r.others_amount || 0),
      money(r.fleet || 0),
      money(r.misc || 0),
      money(r.remaining_balance || 0),
      r.comment || "",
    ]);

    await saveBrandedPdf({
      title: "Reconciliation Report",
      subtitle: `Period: ${formatDateTimeDisplay(startDate)} to ${formatDateTimeDisplay(endDate)}`,
      filename: `Reconciliation_${startDate}_${endDate}.pdf`,
      badgeText: "RECON",
      head: [[
        "ID",
        "Start Time",
        "End Time",
        "Employee Name",
        "Shift",
        "Fuel Amount",
        "Lube Amount",
        "Missing Lube",
        "Total",
        "Discount",
        "Net Sale",
        "Cash",
        "UPI",
        "Card",
        "HP Pay",
        "Credit",
        "Test",
        "Others",
        "Fleet",
        "Misc",
        "Remaining",
        "Comment",
      ]],
      body,
      orientation: "landscape",
      startY: 47,
    });
  };

  // =====================================================================
  // CUSTOMER CREDIT SECTION
  // =====================================================================
  const ccCustomers = useMemo(() => {
    const map = {};
    custCreditRows.forEach((r) => {
      if (!r.customer_id) return;
      map[r.customer_id] = r.customer_name || `Customer #${r.customer_id}`;
    });
    return Object.entries(map)
      .map(([id, name]) => ({ id: String(id), name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [custCreditRows]);

  const rowToSearchString = (r) => {
    const fields = [
      r.customer_name,
      r.fuel_type,
      r.volume,
      r.amount,
      r.pending_amount,
      r.credit_status,
      r.credit_comment,
      r.latest_payment_mode,
      r.latest_reference_no,
      r.latest_payment_comment,
      r.latest_payment_status,
      r.latest_payment_date,
      r.credit_created_at,
      r.creditor_days,
    ];
    return fields
      .filter((x) => x !== null && x !== undefined)
      .join(" ")
      .toLowerCase();
  };

  const filteredCustomerCredits = useMemo(() => {
    const s = moment(startDate);
    const e = moment(endDate);
    const q = (ccSearch || "").trim().toLowerCase();

    return (custCreditRows || [])
      .filter((r) => {
        const created = r.credit_created_at || r.created_at;
        if (created) {
          const m = moment(created);
          if (!m.isValid()) return false;
          if (m.isBefore(s) || m.isAfter(e)) return false;
        }
        return true;
      })
      .filter((r) => {
        if (ccCustomerId === "ALL") return true;
        return String(r.customer_id) === String(ccCustomerId);
      })
      .filter((r) => {
        if (!q) return true;
        return rowToSearchString(r).includes(q);
      })
      .sort((a, b) => {
        const ad = a.credit_created_at || a.created_at;
        const bd = b.credit_created_at || b.created_at;
        return moment(bd).valueOf() - moment(ad).valueOf();
      });
  }, [custCreditRows, startDate, endDate, ccCustomerId, ccSearch]);

  const sortedCustomerCredits = useMemo(
    () => sortRowsByConfig(filteredCustomerCredits, tableSorts.custcredit),
    [filteredCustomerCredits, tableSorts.custcredit]
  );

  const currentCustomerCredits = sortedCustomerCredits.slice(
    ccOffset,
    ccOffset + ccItemsPerPage
  );
  const ccPageCount =
    Math.ceil(sortedCustomerCredits.length / ccItemsPerPage) || 0;

  const filteredAttendanceRows = useMemo(() => {
    const q = String(attendanceSearch || "").trim().toLowerCase();

    return (attendanceRows || []).filter((row) => {
      if (!q) return true;
      return [
        row.employee_name,
        row.role,
        row.shift_type,
        row.date_of_joining,
        row.salary_credit_day,
        row.last_present_date,
        row.salary,
        row.present_days,
        row.absent_days,
        row.attendance_rate,
        row.gross_payable,
        row.credit_added,
        row.credit_recovered,
        row.credit_deduction,
        row.running_credit_balance,
        row.net_payable,
      ]
        .filter((value) => value !== null && value !== undefined)
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [attendanceRows, attendanceSearch]);

  const sortedAttendanceRows = useMemo(
    () => sortRowsByConfig(filteredAttendanceRows, tableSorts.attendance),
    [filteredAttendanceRows, tableSorts.attendance]
  );

  const attendanceOffset = attendancePage * attendanceItemsPerPage;
  const currentAttendanceRows = sortedAttendanceRows.slice(
    attendanceOffset,
    attendanceOffset + attendanceItemsPerPage
  );
  const attendancePageCount =
    Math.ceil(sortedAttendanceRows.length / attendanceItemsPerPage) || 0;

  const attendancePayableChartData = useMemo(
    () =>
      sortedAttendanceRows
        .slice()
        .sort((a, b) => Number(b.net_payable || 0) - Number(a.net_payable || 0))
        .slice(0, 8)
        .map((row, index) => ({
          name: row.employee_name,
          value: Number(row.net_payable || 0),
          fill: reportChartColors[index % reportChartColors.length],
        })),
    [sortedAttendanceRows]
  );

  const attendanceShiftChartData = useMemo(
    () =>
      (attendanceSummary.shiftCounts || []).map((row, index) => ({
        name: row.shift || "-",
        value: Number(row.count || 0),
        fill: reportChartColors[index % reportChartColors.length],
      })),
    [attendanceSummary.shiftCounts]
  );

  const ccTotals = useMemo(() => {
    const sum = (k) =>
      filteredCustomerCredits.reduce((acc, r) => acc + (Number(r?.[k]) || 0), 0);

    return {
      total_amount: sum("amount"),
      total_pending: sum("pending_amount"),
      total_paid: sum("total_paid"),
      avg_days:
        filteredCustomerCredits.length > 0
          ? (
              filteredCustomerCredits.reduce(
                (acc, r) => acc + (Number(r.creditor_days) || 0),
                0
              ) / filteredCustomerCredits.length
            ).toFixed(1)
          : "0.0",
    };
  }, [filteredCustomerCredits]);

  const ccByCustomerAmount = useMemo(() => {
    const map = {};
    filteredCustomerCredits.forEach((r) => {
      const name = r.customer_name || `Customer #${r.customer_id}`;
      if (!map[name]) map[name] = { name, value: 0 };
      map[name].value += Number(r.amount || 0);
    });
    return Object.values(map).sort((a, b) => b.value - a.value);
  }, [filteredCustomerCredits]);

  const ccByCustomerDays = useMemo(() => {
    const map = {};
    filteredCustomerCredits.forEach((r) => {
      const name = r.customer_name || `Customer #${r.customer_id}`;
      if (!map[name]) map[name] = { customer: name, daysSum: 0, count: 0 };
      map[name].daysSum += Number(r.creditor_days || 0);
      map[name].count += 1;
    });

    return Object.values(map)
      .map((x) => ({
        customer: x.customer,
        avg_days: x.count ? Number((x.daysSum / x.count).toFixed(1)) : 0,
      }))
      .sort((a, b) => b.avg_days - a.avg_days);
  }, [filteredCustomerCredits]);

  const reconCreditCustomerSplit = useMemo(() => {
    const start = moment(startDate);
    const end = moment(endDate);

    return buildCreditSplitRows(
      custCreditRows
      .filter((row) => {
        const dt = row.credit_created_at || row.created_at;
        if (!dt) return true;
        return moment(dt).isBetween(start, end, undefined, "[]");
      })
    );
  }, [custCreditRows, endDate, reportChartColors, startDate]);

  const selectedReconSplitRows = useMemo(
    () => (selectedReconSplit?.rows ? selectedReconSplit.rows : []),
    [selectedReconSplit]
  );

  const selectedReconCreditSplit = useMemo(
    () => buildCreditSplitRows(selectedReconSplitRows),
    [selectedReconSplitRows]
  );

  const selectedReconFuelSplit = useMemo(
    () => buildFuelSplitRows(selectedReconSplit?.fuelRows || []),
    [selectedReconSplit]
  );

  const selectedReconSettlementSplit = useMemo(
    () => buildSettlementSplitRows(selectedReconSplit?.reconciliation),
    [selectedReconSplit]
  );

  const activeReconCreditSplit = selectedReconSplit
    ? selectedReconCreditSplit
    : reconCreditCustomerSplit;

  const reportContext = useMemo(() => {
    const base = {
      title: "Reports",
      description:
        "Premium reporting hub for sales, reconciliation, credit ledgers, and profitability.",
      badge: "REPORTS",
    };

    if (activeTab === "nozzle") {
      return {
        ...base,
        title: "Nozzle Report",
        description:
          "Track gross, testing, and actual sale movement with fuel-wise performance at a glance.",
        badge: "NOZZLE",
      };
    }

    if (activeTab === "lube") {
      return {
        ...base,
        title: "Lube Sales Report",
        description:
          "See product mix, operator activity, and total lube contribution in one compact view.",
        badge: "LUBE",
      };
    }

    if (activeTab === "pnl") {
      return {
        ...base,
        title: "Profit & Loss Dashboard",
        description:
          "A command center for revenue, margins, expenses, and net profit trends.",
        badge: "P&L",
      };
    }

    if (activeTab === "recon") {
      return {
        ...base,
        title: "Reconciliation Report",
        description:
          "Understand settlement balance, payment mix, and employee-wise totals without friction.",
        badge: "RECON",
      };
    }

    if (activeTab === "attendance") {
      return {
        ...base,
        title: "Attendance Report",
        description:
          "Track presence from reconciliations, shift coverage, and payroll-ready payable amounts in one place.",
        badge: "ATTENDANCE",
      };
    }

    if (activeTab === "custcredit") {
      return {
        ...base,
        title: "Customer Credit",
        description:
          "Review outstanding credit, payment flow, and ageing with a clean ledger-like rhythm.",
        badge: "CREDIT",
      };
    }

    if (activeTab === "passbook") {
      return {
        ...base,
        title: "Customer Passbook",
        description:
          "A ledger-style customer credit experience with the full transaction trail.",
        badge: "PASSBOOK",
      };
    }

    return base;
  }, [activeTab]);

  const reportSummaryCards = useMemo(() => {
    if (activeTab === "nozzle") {
      return [
        { label: "Gross Volume", value: `${money(totalGrossVolume)} L`, tone: "primary" },
        { label: "Testing Volume", value: `${money(totalTestingVolume)} L`, tone: "warning" },
        { label: "Actual Amount", value: `₹${money(totalActualAmount)}`, tone: "success" },
        { label: "Fuel Types", value: String(Object.keys(totalsByFuel).length), tone: "dark" },
      ];
    }

    if (activeTab === "lube") {
      return [
        { label: "Total Qty", value: money(lubeTotals.total_quantity), tone: "primary" },
        { label: "Total Amount", value: `₹${money(lubeTotals.total_amount)}`, tone: "success" },
        { label: "Products", value: String(lubeSummary.length || 0), tone: "warning" },
        { label: "Rows", value: String(lubeSales.length || 0), tone: "dark" },
      ];
    }

    if (activeTab === "pnl") {
      return [
        { label: "Revenue", value: `₹${money(pnl ? pnl.gross_total_revenue : 0)}`, tone: "primary" },
        { label: "Gross Margin", value: `₹${money(pnl ? pnl.total_gross_margin : 0)}`, tone: "success" },
        { label: "Operating Profit", value: `₹${money(pnl ? pnl.operating_profit : 0)}`, tone: "warning" },
        { label: "Margin %", value: `${profitMarginPct}%`, tone: "dark" },
      ];
    }

    if (activeTab === "recon") {
      return [
        { label: "Net Sale", value: `₹${money(reconTotals.net_sale)}`, tone: "primary" },
        { label: "Cash", value: `₹${money(reconTotals.cash_amount)}`, tone: "success" },
        { label: "UPI", value: `₹${money(reconTotals.upi_amount)}`, tone: "warning" },
        { label: "Remaining", value: `₹${money(reconTotals.remaining_balance)}`, tone: "dark" },
      ];
    }

    if (activeTab === "attendance") {
      return [
        { label: "Present Days", value: String(attendanceSummary.presentEmployeeDays || 0), tone: "success" },
        { label: "Absent Days", value: String(attendanceSummary.absentEmployeeDays || 0), tone: "warning" },
        { label: "Gross Payable", value: `₹${money(attendanceSummary.grossPayable)}`, tone: "primary" },
        { label: "Net Payable", value: `₹${money(attendanceSummary.netPayable)}`, tone: "dark" },
      ];
    }

    if (activeTab === "custcredit") {
      return [
        { label: "Total Credit", value: `₹${money(ccTotals.total_amount)}`, tone: "primary" },
        { label: "Total Paid", value: `₹${money(ccTotals.total_paid)}`, tone: "success" },
        { label: "Pending", value: `₹${money(ccTotals.total_pending)}`, tone: "warning" },
        { label: "Avg Days", value: `${ccTotals.avg_days}`, tone: "dark" },
      ];
    }

    return [];
  }, [
    activeTab,
    ccTotals.avg_days,
    ccTotals.total_amount,
    ccTotals.total_paid,
    ccTotals.total_pending,
    lubeSales.length,
    lubeSummary.length,
    lubeTotals.total_amount,
    lubeTotals.total_quantity,
    money,
    pnl,
    profitMarginPct,
    attendanceSummary.absentEmployeeDays,
    attendanceSummary.grossPayable,
    attendanceSummary.netPayable,
    attendanceSummary.presentEmployeeDays,
    reconTotals.cash_amount,
    reconTotals.net_sale,
    reconTotals.remaining_balance,
    reconTotals.upi_amount,
    totalActualAmount,
    totalGrossVolume,
    totalTestingVolume,
    totalsByFuel,
  ]);

  const handleRefreshActiveTab = () => {
    try {
      if (activeTab === "nozzle") return fetchReport();
      if (activeTab === "lube") return fetchLubeReport();
      if (activeTab === "pnl") return fetchPnL();
      if (activeTab === "recon") return fetchReconReport();
      if (activeTab === "attendance") return fetchAttendanceReport();
      if (activeTab === "custcredit") return fetchCustomerCreditReport();
      return null;
    } catch (err) {
      console.error("Refresh failed:", err);
      return null;
    }
  };

  const handleExcelExport = () => {
    try {
      if (activeTab === "nozzle") return exportNozzleToExcel();
      if (activeTab === "lube") return exportLubeToExcel();
      if (activeTab === "pnl") return exportPnlToExcel();
      if (activeTab === "recon") return exportReconToExcel();
      if (activeTab === "attendance") return exportAttendanceToExcel();
      if (activeTab === "custcredit") return exportCustomerCreditToExcel();
      return null;
    } catch (err) {
      console.error("Excel export failed:", err);
      return null;
    }
  };

  const handlePdfExport = () => {
    try {
      if (activeTab === "nozzle") return exportNozzleToPDF();
      if (activeTab === "lube") return exportLubeToPDF();
      if (activeTab === "pnl") return exportPnlToPDF();
      if (activeTab === "recon") return exportReconToPDF();
      if (activeTab === "attendance") return exportAttendanceToPDF();
      if (activeTab === "custcredit") return exportCustomerCreditToPDF();
      return null;
    } catch (err) {
      console.error("PDF export failed:", err);
      return null;
    }
  };

 



  const exportCustomerCreditToExcel = async () => {
    if (!filteredCustomerCredits || filteredCustomerCredits.length === 0) return;

    const columns = [
      "Customer Name",
      "Fuel Type",
      "Volume (L)",
      "Credit Amount (₹)",
      "Pending Amount (₹)",
      "Total Paid (₹)",
      "Credit Status",
      "Credit Comment",
      "Credit Created At",
      "Latest Payment Date",
      "Latest Paid At",
      "Latest Amount Paid (₹)",
      "Latest Payment Mode",
      "Latest Reference No",
      "Latest Payment Comment",
      "Latest Payment Status",
      "Creditor Days",
    ];

    const rows = filteredCustomerCredits.map((r) => [
      r.customer_name,
      r.fuel_type || "",
      money(r.volume || 0),
      money(r.amount || 0),
      money(r.pending_amount || 0),
      money(r.total_paid || 0),
      r.credit_status,
      r.credit_comment || "",
      r.credit_created_at || "",
      r.latest_payment_date || "",
      r.latest_paid_at || "",
      money(r.latest_amount_paid || 0),
      r.latest_payment_mode || "",
      r.latest_reference_no || "",
      r.latest_payment_comment || "",
      r.latest_payment_status || "",
      Number(r.creditor_days || 0),
    ]);

    await saveExcelWithBranding({
      filename: `CustomerCredits_${moment(startDate).format("DDMMYYYY")}_${moment(endDate).format("DDMMYYYY")}.xlsx`,
      sheetName: "CustomerCredits",
      title: "Customer Credit Report",
      subtitle: `Period: ${formatDateTimeDisplay(startDate)} to ${formatDateTimeDisplay(endDate)}`,
      rows,
      columns,
      columnWidths: [24, 14, 12, 18, 18, 16, 14, 24, 20, 18, 18, 20, 18, 20, 24, 18, 14],
    });
  };

  const exportCustomerCreditToPDF = async () => {
    if (!filteredCustomerCredits || filteredCustomerCredits.length === 0) return;

    const body = filteredCustomerCredits.map((r) => [
      r.customer_name || "-",
      r.fuel_type || "-",
      money(r.volume || 0),
      money(r.amount || 0),
      money(r.pending_amount || 0),
      money(r.total_paid || 0),
      r.credit_status || "-",
      stripHtml(r.credit_comment || ""),
      r.credit_created_at ? moment(r.credit_created_at).format("YYYY-MM-DD HH:mm") : "-",
      r.latest_payment_date ? moment(r.latest_payment_date).format("YYYY-MM-DD") : "-",
      r.latest_payment_mode || "-",
      r.latest_reference_no || "-",
      stripHtml(r.latest_payment_comment || ""),
      Number(r.creditor_days || 0).toFixed(0),
    ]);

    await saveBrandedPdf({
      title: "Customer Credit Report",
      subtitle: `Period: ${formatDateTimeDisplay(startDate)} to ${formatDateTimeDisplay(endDate)}`,
      filename: `CustomerCredits_${moment(startDate).format("DDMMYYYY")}_${moment(endDate).format("DDMMYYYY")}.pdf`,
      badgeText: "CREDIT",
      head: [[
        "Customer",
        "Fuel Type",
        "Volume",
        "Credit Amount",
        "Pending",
        "Total Paid",
        "Status",
        "Credit Comment",
        "Credit Created",
        "Latest Payment Date",
        "Latest Mode",
        "Latest Ref",
        "Latest Payment Comment",
        "Creditor Days",
      ]],
      body,
      orientation: "landscape",
      startY: 47,
    });
  };

  const exportAttendanceToExcel = async () => {
    if (!sortedAttendanceRows || sortedAttendanceRows.length === 0) return;

    const columns = [
      "Employee",
      "Role",
      "Default Shift",
      "Date Of Joining",
      "Salary Credit Day",
      "Monthly Salary (₹)",
      "Eligible Days",
      "Present Days",
      "Absent Days",
      "Attendance %",
      "Gross Payable (₹)",
      "Credit Added (₹)",
      "Credit Recovered (₹)",
      "Credit Deduction (₹)",
      "Running Credit Balance (₹)",
      "Net Payable (₹)",
      "Last Present Date",
    ];

    const rows = sortedAttendanceRows.map((row) => [
      row.employee_name || "-",
      row.role || "-",
      row.shift_type || "-",
      row.date_of_joining || "-",
      row.salary_credit_day || "-",
      money(row.salary || 0),
      Number(row.eligible_days || 0),
      Number(row.present_days || 0),
      Number(row.absent_days || 0),
      Number(row.attendance_rate || 0).toFixed(2),
      money(row.gross_payable || 0),
      money(row.credit_added || 0),
      money(row.credit_recovered || 0),
      money(row.credit_deduction || 0),
      money(row.running_credit_balance || 0),
      money(row.net_payable || 0),
      row.last_present_date || "-",
    ]);

    await saveExcelWithBranding({
      filename: `AttendanceReport_${moment(startDate).format("YYYYMMDD_HHmm")}_${moment(endDate).format("YYYYMMDD_HHmm")}.xlsx`,
      sheetName: "Attendance",
      title: "Attendance Report",
      subtitle: `Period: ${formatDateTimeDisplay(startDate)} to ${formatDateTimeDisplay(endDate)} | Shift: ${attendanceShiftFilter === "ALL" ? "All" : attendanceShiftFilter}`,
      rows,
      columns,
      columnWidths: [22, 12, 14, 14, 14, 16, 12, 12, 12, 12, 16, 16, 16, 16, 18, 16, 14],
    });
  };

  const exportAttendanceToPDF = async () => {
    if (!sortedAttendanceRows || sortedAttendanceRows.length === 0) return;

    const body = sortedAttendanceRows.map((row) => [
      row.employee_name || "-",
      row.role || "-",
      row.shift_type || "-",
      row.date_of_joining || "-",
      Number(row.present_days || 0),
      Number(row.absent_days || 0),
      Number(row.attendance_rate || 0).toFixed(2),
      money(row.gross_payable || 0),
      money(row.credit_deduction || 0),
      money(row.running_credit_balance || 0),
      money(row.net_payable || 0),
      row.last_present_date || "-",
    ]);

    await saveBrandedPdf({
      title: "Attendance Report",
      subtitle: `Period: ${formatDateTimeDisplay(startDate)} to ${formatDateTimeDisplay(endDate)} | Shift: ${attendanceShiftFilter === "ALL" ? "All" : attendanceShiftFilter}`,
      filename: `AttendanceReport_${moment(startDate).format("YYYYMMDD_HHmm")}_${moment(endDate).format("YYYYMMDD_HHmm")}.pdf`,
      badgeText: "ATTENDANCE",
      head: [[
        "Employee",
        "Role",
        "Default Shift",
        "DOJ",
        "Present",
        "Absent",
        "Attendance %",
        "Gross Payable",
        "Credit Deduction",
        "Running Credit",
        "Net Payable",
        "Last Present",
      ]],
      body,
      orientation: "landscape",
      startY: 47,
    });
  };



  // =====================================================================
  // RENDER
  // =====================================================================
  return (
    <div className="report-page">
      <div className="report-tabs-wrap">
        <ul className="nav nav-tabs report-tabs mb-3">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "nozzle" ? "active" : ""}`}
              onClick={() => setActiveTab("nozzle")}
            >
              Nozzle Report
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "lube" ? "active" : ""}`}
              onClick={() => setActiveTab("lube")}
            >
              Lube Report
            </button>
          </li>
          {canViewPnl && (
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "pnl" ? "active" : ""}`}
                onClick={() => setActiveTab("pnl")}
              >
                P &amp; L Dashboard
              </button>
            </li>
          )}

          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "recon" ? "active" : ""}`}
              onClick={() => setActiveTab("recon")}
            >
              Reconciliation Report
            </button>
          </li>

          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "attendance" ? "active" : ""}`}
              onClick={() => {
                setActiveTab("attendance");
                if (!attendanceRows || attendanceRows.length === 0) {
                  fetchAttendanceReport();
                }
              }}
            >
              Attendance Report
            </button>
          </li>

          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "custcredit" ? "active" : ""}`}
              onClick={() => {
                setActiveTab("custcredit");
                if (!custCreditRows || custCreditRows.length === 0) {
                  fetchCustomerCreditReport();
                }
              }}
            >
              Customer Credit
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "passbook" ? "active" : ""}`}
              onClick={() => setActiveTab("passbook")}
            >
              Customer Passbook
            </button>
          </li>
        </ul>
      </div>

      <section className="report-hero" style={{ borderTop: `4px solid ${reportAccent}` }}>
        <div className="report-hero-copy">
          <span className="report-eyebrow">{reportContext.badge}</span>
          <h1>{reportContext.title}</h1>
          <p>{reportContext.description}</p>
          <div className="report-meta">
            <span>{bunkName}</span>
            <span>{bunkAddress}</span>
            <span>{formatDateTimeDisplay(startDate)} to {formatDateTimeDisplay(endDate)}</span>
          </div>
        </div>

        <div className="report-hero-brand">
          <div className="report-logo-row">
            <img src={FuelFlowLogo} alt="FuelFlow" />
            <img src={activeCompanyLogo} alt={companyName} />
          </div>
          <div className="report-hero-actions">
            <button
              className="btn btn-light"
              onClick={handleRefreshActiveTab}
              type="button"
            >
              Refresh
            </button>
            <button
              className="btn btn-success"
              onClick={handleExcelExport}
              type="button"
              disabled={
                (activeTab === "nozzle" && (!data || data.length === 0)) ||
                (activeTab === "lube" && (!lubeSales || lubeSales.length === 0)) ||
                (activeTab === "pnl" && !pnl) ||
                (activeTab === "recon" && (!reconRows || reconRows.length === 0)) ||
                (activeTab === "attendance" && (!sortedAttendanceRows || sortedAttendanceRows.length === 0)) ||
                (activeTab === "custcredit" && (!filteredCustomerCredits || filteredCustomerCredits.length === 0)) ||
                activeTab === "passbook"
              }
            >
              Excel
            </button>
            <button
              className="btn btn-danger"
              onClick={handlePdfExport}
              type="button"
              disabled={
                (activeTab === "nozzle" && (!data || data.length === 0)) ||
                (activeTab === "lube" && (!lubeSales || lubeSales.length === 0)) ||
                (activeTab === "pnl" && !pnl) ||
                (activeTab === "recon" && (!reconRows || reconRows.length === 0)) ||
                (activeTab === "attendance" && (!sortedAttendanceRows || sortedAttendanceRows.length === 0)) ||
                (activeTab === "custcredit" && (!filteredCustomerCredits || filteredCustomerCredits.length === 0)) ||
                activeTab === "passbook"
              }
            >
              PDF
            </button>
          </div>
        </div>
      </section>

      <section className="report-context-strip">
        <div className="report-context-left">
          <span className="report-context-label">Current context</span>
          <strong>{reportContext.title}</strong>
          <p>{reportContext.description}</p>
        </div>
        <div className="report-context-right">
          <div className="report-context-chip">
            <span>Bunk</span>
            <strong>{bunkName}</strong>
          </div>
          <div className="report-context-chip">
            <span>Phone</span>
            <strong>{adminPhone}</strong>
          </div>
          <div className="report-context-chip">
            <span>Company</span>
            <strong>{companyName}</strong>
          </div>
        </div>
      </section>

      {reportSummaryCards.length > 0 && (
        <section className="report-summary-grid">
          {reportSummaryCards.map((card) => (
            <article key={card.label} className={`report-summary-card tone-${card.tone}`}>
              <span>{card.label}</span>
              <strong>{card.value}</strong>
            </article>
          ))}
        </section>
      )}

      {activeTab === "nozzle" && (
        <div className="row mb-3 g-2">
          <div className="col-md-3">
            <label className="form-label">Start Date &amp; Time</label>
            <input
              type="datetime-local"
              className="form-control"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="col-md-3">
            <label className="form-label">End Date &amp; Time</label>
            <input
              type="datetime-local"
              className="form-control"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <div className="col-md-3">
            <label className="form-label">Fuel Type</label>
            <select
              className="form-select"
              value={fuelType}
              onChange={(e) => setFuelType(e.target.value)}
            >
              <option value="ALL">All</option>
              <option value="Petrol">Petrol</option>
              <option value="Diesel">Diesel</option>
              <option value="Power Petrol">Power Petrol</option>
              <option value="Power Diesel">Power Diesel</option>
              <option value="CNG">CNG</option>
              <option value="Diesel 100">Diesel 100</option>
            </select>
          </div>

          <div className="col-md-3 d-flex align-items-end">
            <button
              onClick={fetchReport}
              className="btn btn-primary w-100"
              disabled={loading}
            >
              {loading ? "Loading..." : "Generate Report"}
            </button>
          </div>
        </div>
      )}

      {activeTab === "lube" && (
        <div className="row mb-3 g-2">
          <div className="col-md-4">
            <label className="form-label">Start Date &amp; Time</label>
            <input
              type="datetime-local"
              className="form-control"
              value={lubeStartDateTime}
              onChange={(e) => setLubeStartDateTime(e.target.value)}
            />
          </div>

          <div className="col-md-4">
            <label className="form-label">End Date &amp; Time</label>
            <input
              type="datetime-local"
              className="form-control"
              value={lubeEndDateTime}
              onChange={(e) => setLubeEndDateTime(e.target.value)}
            />
          </div>

          <div className="col-md-4 d-flex align-items-end">
            <button
              onClick={fetchLubeReport}
              className="btn btn-primary w-100"
              disabled={lubeLoading}
            >
              {lubeLoading ? "Loading..." : "Generate Lube Report"}
            </button>
          </div>
        </div>
      )}





      {activeTab === "pnl" && (
        <div className="row mb-3 g-2">
          <div className="col-md-3">
            <label className="form-label">Start Date &amp; Time</label>
            <input
              type="datetime-local"
              className="form-control"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="col-md-3">
            <label className="form-label">End Date &amp; Time</label>
            <input
              type="datetime-local"
              className="form-control"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <div className="col-md-3 d-flex align-items-end">
            <button
              onClick={() => fetchPnL()}
              className="btn btn-primary w-100"
              disabled={pnlLoading}
            >
              {pnlLoading ? "Loading..." : "Generate P&L"}
            </button>
          </div>
        </div>
      )}

      {activeTab === "attendance" && (
        <>
          <div className="row mb-3 g-2">
            <div className="col-lg-3 col-md-6">
              <label className="form-label">Start Date &amp; Time</label>
              <input
                type="datetime-local"
                className="form-control"
                value={startDate}
                onChange={(e) => {
                  setAttendancePreset("custom");
                  setStartDate(e.target.value);
                }}
              />
            </div>

            <div className="col-lg-3 col-md-6">
              <label className="form-label">End Date &amp; Time</label>
              <input
                type="datetime-local"
                className="form-control"
                value={endDate}
                onChange={(e) => {
                  setAttendancePreset("custom");
                  setEndDate(e.target.value);
                }}
              />
            </div>

            <div className="col-lg-2 col-md-4">
              <label className="form-label">Shift</label>
              <select
                className="form-select"
                value={attendanceShiftFilter}
                onChange={(e) => setAttendanceShiftFilter(e.target.value)}
              >
                <option value="ALL">All</option>
                <option value="morning">Morning</option>
                <option value="night">Night</option>
                <option value="24Hours">24 Hours</option>
              </select>
            </div>

            <div className="col-lg-2 col-md-4">
              <label className="form-label">Search</label>
              <input
                className="form-control"
                placeholder="Employee or shift"
                value={attendanceSearch}
                onChange={(e) => {
                  setAttendanceSearch(e.target.value);
                  setAttendancePage(0);
                }}
              />
            </div>

            <div className="col-lg-2 col-md-4 d-flex align-items-end">
              <button
                onClick={() => fetchAttendanceReport()}
                className="btn btn-primary w-100"
                disabled={attendanceLoading}
              >
                {attendanceLoading ? "Loading..." : "Generate Report"}
              </button>
            </div>
          </div>

          <div className="d-flex flex-wrap gap-2 mb-3">
            <button
              className={`btn btn-outline-secondary ${attendancePreset === "week" ? "active" : ""}`}
              onClick={() => applyAttendancePreset("week")}
              type="button"
            >
              This Week
            </button>
            <button
              className={`btn btn-outline-secondary ${attendancePreset === "15days" ? "active" : ""}`}
              onClick={() => applyAttendancePreset("15days")}
              type="button"
            >
              Last 15 Days
            </button>
            <button
              className={`btn btn-outline-secondary ${attendancePreset === "month" ? "active" : ""}`}
              onClick={() => applyAttendancePreset("month")}
              type="button"
            >
              This Month
            </button>
          </div>
        </>
      )}

      {activeTab === "recon" && (
        <div className="row mb-3 g-2">
          <div className="col-md-3">
            <label className="form-label">Start Date &amp; Time</label>
            <input
              type="datetime-local"
              className="form-control"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="col-md-3">
            <label className="form-label">End Date &amp; Time</label>
            <input
              type="datetime-local"
              className="form-control"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <div className="col-md-3 d-flex align-items-end">
            <button
              onClick={fetchReconReport}
              className="btn btn-primary w-100"
              disabled={reconLoading}
            >
              {reconLoading ? "Loading..." : "Generate Recon Report"}
            </button>
          </div>

          <div className="col-md-3 d-flex align-items-end">
            <button
              onClick={exportReconToExcel}
              className="btn btn-success w-100"
              disabled={!reconRows || reconRows.length === 0}
            >
              Export Recon (Excel)
            </button>
          </div>
        </div>
      )}

      {activeTab === "custcredit" && (
        <div className="row mb-3 g-2">
          <div className="col-md-3">
            <label className="form-label">Start Date &amp; Time</label>
            <input
              type="datetime-local"
              className="form-control"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="col-md-3">
            <label className="form-label">End Date &amp; Time</label>
            <input
              type="datetime-local"
              className="form-control"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <div className="col-md-3">
            <label className="form-label">Customer</label>
            <select
              className="form-select"
              value={ccCustomerId}
              onChange={(e) => {
                setCcCustomerId(e.target.value);
                setCcPage(0);
              }}
            >
              <option value="ALL">All Customers</option>
              {ccCustomers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">Search</label>
            <input
              type="text"
              className="form-control"
              placeholder="Search anything…"
              value={ccSearch}
              onChange={(e) => {
                setCcSearch(e.target.value);
                setCcPage(0);
              }}
            />
          </div>

          <div className="col-md-3 d-flex align-items-end">
            <button
              onClick={fetchCustomerCreditReport}
              className="btn btn-primary w-100"
              disabled={custCreditLoading}
            >
              {custCreditLoading ? "Loading..." : "Refresh Customer Credits"}
            </button>
          </div>

          <div className="col-md-3 d-flex align-items-end">
            <button
              onClick={exportCustomerCreditToExcel}
              className="btn btn-success w-100"
              disabled={
                !filteredCustomerCredits || filteredCustomerCredits.length === 0
              }
            >
              Export (Filtered Excel)
            </button>
          </div>
        </div>
      )}

      {activeTab === "passbook" && <CustomerPassbook />}


      {activeTab === "pnl" && (
        <div className="mb-3">
          <div className="btn-group me-2">
            <button
              className={`btn btn-outline-secondary ${
                rangeType === "week" ? "active" : ""
              }`}
              onClick={() => applyRange("week")}
            >
              Last 7 Days
            </button>
            <button
              className={`btn btn-outline-secondary ${
                rangeType === "month" ? "active" : ""
              }`}
              onClick={() => applyRange("month")}
            >
              This Month
            </button>
            <button
              className={`btn btn-outline-secondary ${
                rangeType === "quarter" ? "active" : ""
              }`}
              onClick={() => applyRange("quarter")}
            >
              This Quarter
            </button>
            <button
              className={`btn btn-outline-secondary ${
                rangeType === "year" ? "active" : ""
              }`}
              onClick={() => applyRange("year")}
            >
              This Year
            </button>
          </div>

          <button
            className="btn btn-success me-2"
            onClick={exportPnlToExcel}
            disabled={!pnl}
          >
            Export P&L (Excel)
          </button>
          <button
            className="btn btn-danger"
            onClick={exportPnlToPDF}
            disabled={!pnl}
          >
            Export P&L (PDF)
          </button>
        </div>
      )}

      {activeTab === "nozzle" && (
        <>
          <div className="row g-2 mb-3">
            <div className="col-md-4">
              <div className="alert alert-secondary mb-0">
                <strong>Gross Volume:</strong> {Number(totalGrossVolume).toFixed(3)} L
                <br />
                <strong>Gross Amount:</strong> ₹{Number(totalGrossAmount).toFixed(2)}
              </div>
            </div>

            <div className="col-md-4">
              <div className="alert alert-warning mb-0">
                <strong>Testing Volume:</strong> {Number(totalTestingVolume).toFixed(3)} L
                <br />
                <strong>Testing Amount:</strong> ₹{Number(totalTestingAmount).toFixed(2)}
              </div>
            </div>

            <div className="col-md-4">
              <div className="alert alert-success mb-0">
                <strong>Actual Sale Volume:</strong> {Number(totalActualVolume).toFixed(3)} L
                <br />
                <strong>Actual Sale Amount:</strong> ₹{Number(totalActualAmount).toFixed(2)}
              </div>
            </div>
          </div>

          <div className="d-flex flex-wrap justify-content-between align-items-center mb-2 gap-2">
            <div className="small text-muted">
              Actual sale excludes testing quantity.
            </div>
            <div className="d-flex gap-2">
              <button onClick={exportNozzleToExcel} className="btn btn-success">
                Export to Excel
              </button>
            </div>
          </div>

          {Object.keys(totalsByFuel).length > 0 && (
            <div className="table-responsive mb-3">
              <table className="table table-sm table-bordered">
                <thead className="table-light">
                  <tr>
                    <th>Fuel Type</th>
                    <th>Gross Volume (L)</th>
                    <th>Testing Volume (L)</th>
                    <th>Actual Sale Volume (L)</th>
                    <th>Gross Amount (₹)</th>
                    <th>Testing Amount (₹)</th>
                    <th>Actual Sale Amount (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(totalsByFuel).map(([ft, vals]) => (
                    <tr key={ft}>
                      <td>{ft}</td>
                      <td>{Number(vals.gross_volume).toFixed(3)}</td>
                      <td>{Number(vals.testing_volume).toFixed(3)}</td>
                      <td>{Number(vals.actual_volume).toFixed(3)}</td>
                      <td>{Number(vals.gross_amount).toFixed(2)}</td>
                      <td>{Number(vals.testing_amount).toFixed(2)}</td>
                      <td>{Number(vals.actual_amount).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="table-responsive">
            <table className="table table-striped table-bordered align-middle">
              <thead className="table-light">
                <tr>
                  <th onDoubleClick={() => handleTableSort("nozzle", "nozzle_name")} title="Double-click to sort" style={{ cursor: "pointer" }}>Nozzle{sortIndicator("nozzle", "nozzle_name")}</th>
                  <th onDoubleClick={() => handleTableSort("nozzle", "fuel_type")} title="Double-click to sort" style={{ cursor: "pointer" }}>Fuel Type{sortIndicator("nozzle", "fuel_type")}</th>
                  <th onDoubleClick={() => handleTableSort("nozzle", "pump_name")} title="Double-click to sort" style={{ cursor: "pointer" }}>Pump{sortIndicator("nozzle", "pump_name")}</th>
                  <th onDoubleClick={() => handleTableSort("nozzle", "opening_reading")} title="Double-click to sort" style={{ cursor: "pointer" }}>Opening{sortIndicator("nozzle", "opening_reading")}</th>
                  <th onDoubleClick={() => handleTableSort("nozzle", "closing_reading")} title="Double-click to sort" style={{ cursor: "pointer" }}>Closing{sortIndicator("nozzle", "closing_reading")}</th>
                  <th onDoubleClick={() => handleTableSort("nozzle", "dispensed_volume")} title="Double-click to sort" style={{ cursor: "pointer" }}>Gross Volume (L){sortIndicator("nozzle", "dispensed_volume")}</th>
                  <th onDoubleClick={() => handleTableSort("nozzle", "testing_liters")} title="Double-click to sort" style={{ cursor: "pointer" }}>Testing Volume (L){sortIndicator("nozzle", "testing_liters")}</th>
                  <th onDoubleClick={() => handleTableSort("nozzle", "actual_dispensed_volume")} title="Double-click to sort" style={{ cursor: "pointer" }}>Actual Sale Volume (L){sortIndicator("nozzle", "actual_dispensed_volume")}</th>
                  <th onDoubleClick={() => handleTableSort("nozzle", "amount")} title="Double-click to sort" style={{ cursor: "pointer" }}>Gross Amount (₹){sortIndicator("nozzle", "amount")}</th>
                  <th onDoubleClick={() => handleTableSort("nozzle", "testing_amount")} title="Double-click to sort" style={{ cursor: "pointer" }}>Testing Amount (₹){sortIndicator("nozzle", "testing_amount")}</th>
                  <th onDoubleClick={() => handleTableSort("nozzle", "actual_amount")} title="Double-click to sort" style={{ cursor: "pointer" }}>Actual Sale Amount (₹){sortIndicator("nozzle", "actual_amount")}</th>
                  <th onDoubleClick={() => handleTableSort("nozzle", "closing_reading_datetime")} title="Double-click to sort" style={{ cursor: "pointer" }}>Reading Datetime{sortIndicator("nozzle", "closing_reading_datetime")}</th>
                </tr>
              </thead>
              <tbody>
                {currentData.length === 0 ? (
                  <tr>
                    <td colSpan="12" className="text-center">
                      No data found
                    </td>
                  </tr>
                ) : (
                  currentData.map((r) => (
                    <tr key={r.nozzle_reading_id}>
                      <td>{r.nozzle_name}</td>
                      <td>{r.fuel_type}</td>
                      <td>{r.pump_name || "—"}</td>
                      <td>{r.opening_reading}</td>
                      <td>{r.closing_reading}</td>
                      <td>{Number(r.dispensed_volume ?? 0).toFixed(3)}</td>
                      <td>{Number(r.testing_liters ?? 0).toFixed(3)}</td>
                      <td>
                        {Number(
                          r.actual_dispensed_volume != null
                            ? r.actual_dispensed_volume
                            : r.dispensed_volume ?? 0
                        ).toFixed(3)}
                      </td>
                      <td>{Number(r.amount ?? 0).toFixed(2)}</td>
                      <td>{Number(r.testing_amount ?? 0).toFixed(2)}</td>
                      <td>
                        {Number(
                          r.actual_amount != null
                            ? r.actual_amount
                            : r.amount ?? 0
                        ).toFixed(2)}
                      </td>
                      <td>{r.closing_reading_datetime || "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pageCount > 1 && (
            <div className="d-flex justify-content-center mt-3">
              <ReactPaginate
                previousLabel={"← Prev"}
                nextLabel={"Next →"}
                pageCount={pageCount}
                onPageChange={handlePageClick}
                containerClassName={"pagination"}
                pageClassName={"page-item"}
                pageLinkClassName={"page-link"}
                previousClassName={"page-item"}
                nextClassName={"page-item"}
                previousLinkClassName={"page-link"}
                nextLinkClassName={"page-link"}
                activeClassName={"active"}
              />
            </div>
          )}
        </>
      )}

      {activeTab === "lube" && (
        <>
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-2 gap-2">
            <div className="alert alert-info flex-grow-1 mb-0">
              <strong>Total Lube Quantity:</strong>{" "}
              {Number(lubeTotals.total_quantity || 0).toFixed(2)} |{" "}
              <strong>Total Lube Amount:</strong> ₹
              {Number(lubeTotals.total_amount || 0).toFixed(2)}
            </div>
            <button
              onClick={exportLubeToExcel}
              className="btn btn-success"
              disabled={!lubeSales || lubeSales.length === 0}
            >
              Export Lube (Excel)
            </button>
          </div>

          {lubeSummary && lubeSummary.length > 0 && (
            <div className="table-responsive mb-3">
              <table className="table table-sm table-bordered">
                <thead className="table-light">
                  <tr>
                    <th>Product</th>
                    <th>Brand</th>
                    <th>Unit</th>
                    <th>Unit Price (₹)</th>
                    <th>Total Qty</th>
                    <th>Total Amount (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {lubeSummary.map((row) => (
                    <tr key={row.product_id}>
                      <td>{row.product_name}</td>
                      <td>{row.brand}</td>
                      <td>{row.unit}</td>
                      <td>{Number(row.unit_price || 0).toFixed(2)}</td>
                      <td>{Number(row.total_quantity_sold || 0).toFixed(2)}</td>
                      <td>{Number(row.total_sales_amount || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="table-responsive">
            <table className="table table-striped table-bordered align-middle">
              <thead className="table-light">
                <tr>
                  <th onDoubleClick={() => handleTableSort("lube", "sale_date")} title="Double-click to sort" style={{ cursor: "pointer" }}>Sale Date{sortIndicator("lube", "sale_date")}</th>
                  <th onDoubleClick={() => handleTableSort("lube", "shift")} title="Double-click to sort" style={{ cursor: "pointer" }}>Shift{sortIndicator("lube", "shift")}</th>
                  <th onDoubleClick={() => handleTableSort("lube", "operator_name")} title="Double-click to sort" style={{ cursor: "pointer" }}>Operator{sortIndicator("lube", "operator_name")}</th>
                  <th onDoubleClick={() => handleTableSort("lube", "product_name")} title="Double-click to sort" style={{ cursor: "pointer" }}>Product{sortIndicator("lube", "product_name")}</th>
                  <th onDoubleClick={() => handleTableSort("lube", "brand")} title="Double-click to sort" style={{ cursor: "pointer" }}>Brand{sortIndicator("lube", "brand")}</th>
                  <th onDoubleClick={() => handleTableSort("lube", "quantity")} title="Double-click to sort" style={{ cursor: "pointer" }}>Qty{sortIndicator("lube", "quantity")}</th>
                  <th onDoubleClick={() => handleTableSort("lube", "unit_price")} title="Double-click to sort" style={{ cursor: "pointer" }}>Unit Price (₹){sortIndicator("lube", "unit_price")}</th>
                  <th onDoubleClick={() => handleTableSort("lube", "total_amount")} title="Double-click to sort" style={{ cursor: "pointer" }}>Total Amount (₹){sortIndicator("lube", "total_amount")}</th>
                  <th onDoubleClick={() => handleTableSort("lube", "comment")} title="Double-click to sort" style={{ cursor: "pointer" }}>Comment{sortIndicator("lube", "comment")}</th>
                </tr>
              </thead>
              <tbody>
                {currentLubeSales.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center">
                      No data found
                    </td>
                  </tr>
                ) : (
                  currentLubeSales.map((r) => (
                    <tr key={r.sale_id}>
                      <td>{r.sale_date}</td>
                      <td>{r.shift}</td>
                      <td>{r.operator_name || r.operator_id}</td>
                      <td>{r.product_name}</td>
                      <td>{r.brand}</td>
                      <td>{r.quantity}</td>
                      <td>{Number(r.unit_price || 0).toFixed(2)}</td>
                      <td>{Number(r.total_amount || 0).toFixed(2)}</td>
                      <td>{r.comment || ""}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {lubePageCount > 1 && (
            <div className="d-flex justify-content-center mt-3">
              <ReactPaginate
                previousLabel={"← Prev"}
                nextLabel={"Next →"}
                pageCount={lubePageCount}
                onPageChange={handleLubePageClick}
                containerClassName={"pagination"}
                pageClassName={"page-item"}
                pageLinkClassName={"page-link"}
                previousClassName={"page-item"}
                nextClassName={"page-item"}
                previousLinkClassName={"page-link"}
                nextLinkClassName={"page-link"}
                activeClassName={"active"}
              />
            </div>
          )}
        </>
      )}

      {activeTab === "pnl" && (
        <>
          <div className="row g-3 mb-3">
            <div className="col-md-3">
              <div className="card border-primary">
                <div className="card-body">
                  <h6 className="card-title">Total Revenue</h6>
                  <h4>
                    ₹{pnl ? Number(pnl.gross_total_revenue || 0).toFixed(2) : "0.00"}
                  </h4>
                  {pnl && (
                    <small className="text-danger d-block mt-1">
                      Discount: ₹{Number(pnl.total_discount || 0).toFixed(2)}
                    </small>
                  )}
                  {pnl && (
                    <small className="text-success d-block">
                      Net Revenue: ₹{Number(pnl.net_total_revenue || 0).toFixed(2)}
                    </small>
                  )}
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card border-success">
                <div className="card-body">
                  <h6 className="card-title">Gross Margin</h6>
                  <h4>
                    ₹
                    {pnl
                      ? Number(pnl.total_gross_margin || 0).toFixed(2)
                      : "0.00"}
                  </h4>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card border-warning">
                <div className="card-body">
                  <h6 className="card-title">Operating Profit</h6>
                  <h4>
                    ₹
                    {pnl
                      ? Number(pnl.operating_profit || 0).toFixed(2)
                      : "0.00"}
                  </h4>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card border-info">
                <div className="card-body">
                  <h6 className="card-title">Profit Margin %</h6>
                  <h4>{profitMarginPct}%</h4>
                </div>
              </div>
            </div>
          </div>

          <div className="row g-3 mb-4">
            <div className="col-md-3">
              <div className="card border-dark">
                <div className="card-body">
                  <h6 className="card-title">
                    Projected Revenue (30-day run rate)
                  </h6>
                  <h4>₹{projectedRevenue.toFixed(2)}</h4>
                </div>
              </div>
            </div>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-md-8">
              <div className="card report-chart-card">
                <div className="card-body">
                  <div className="report-chart-head">
                    <div>
                      <span>Revenue trend</span>
                      <h6>Fuel revenue movement over time</h6>
                    </div>
                    <small>Actual revenue trend for the selected period</small>
                  </div>
                  <div className="report-chart-shell">
                    <ResponsiveContainer>
                      <AreaChart data={revenueSeries}>
                        <defs>
                          <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#2563eb" stopOpacity={0.34} />
                            <stop offset="95%" stopColor="#2563eb" stopOpacity={0.03} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.08)" />
                        <XAxis dataKey="label" tick={{ fill: "#475569", fontSize: 12, fontWeight: 700 }} />
                        <YAxis tick={{ fill: "#475569", fontSize: 12, fontWeight: 700 }} tickFormatter={(value) => money(value)} />
                        <Tooltip content={modernTooltip} />
                        <Area
                          type="monotone"
                          dataKey="revenue"
                          stroke="#2563eb"
                          strokeWidth={3}
                          fill="url(#revenueGradient)"
                          activeDot={{ r: 6 }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="card report-chart-card">
                <div className="card-body">
                  <div className="report-chart-head">
                    <div>
                      <span>Revenue mix</span>
                      <h6>Fuel, lube, and discount contribution</h6>
                    </div>
                  </div>
                  <div className="report-chart-shell">
                    <ResponsiveContainer>
                      <BarChart data={revenueBreakdown}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.08)" />
                        <XAxis dataKey="name" tick={{ fill: "#475569", fontSize: 12, fontWeight: 700 }} />
                        <YAxis tick={{ fill: "#475569", fontSize: 12, fontWeight: 700 }} tickFormatter={(value) => money(value)} />
                        <Tooltip content={modernTooltip} />
                        <Bar dataKey="revenue" radius={[12, 12, 4, 4]}>
                          {revenueBreakdown.map((entry, index) => (
                            <Cell
                              key={`revenue-cell-${index}`}
                              fill={reportChartColors[index % reportChartColors.length]}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-md-5">
              <div className="card report-chart-card">
                <div className="card-body">
                  <div className="report-chart-head">
                    <div>
                      <span>Gross mix</span>
                      <h6>Expense pressure versus revenue</h6>
                    </div>
                  </div>
                  <div className="report-chart-shell">
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          data={expenseBreakdown}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={72}
                          outerRadius={104}
                          paddingAngle={4}
                          labelLine={false}
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {expenseBreakdown.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={reportChartColors[index % reportChartColors.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip content={modernTooltip} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === "attendance" && (
        <>
          <div className="row g-3 mb-3">
            <div className="col-md-4">
              <div className="alert alert-light border h-100 mb-0">
                <strong>Payroll Window</strong>
                <div className="text-muted small mt-1">
                  {attendancePeriod
                    ? `${attendancePeriod.startDate || "-"} to ${attendancePeriod.endDate || "-"}`
                    : `${moment(startDate).format("YYYY-MM-DD")} to ${moment(endDate).format("YYYY-MM-DD")}`}
                </div>
                <div className="text-muted small">
                  Shift: {attendanceShiftFilter === "ALL" ? "All shifts" : attendanceShiftFilter}
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="alert alert-warning border h-100 mb-0">
                <strong>Credit Impact</strong>
                <div className="small mt-1">
                  Added: ₹{money(attendanceSummary.creditAdded || 0)}
                </div>
                <div className="small">
                  Recovered: ₹{money(attendanceSummary.creditRecovered || 0)}
                </div>
                <div className="small">
                  Deduction used: ₹{money(attendanceSummary.creditDeduction || 0)}
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="alert alert-info border h-100 mb-0">
                <strong>Extra visibility</strong>
                <div className="small mt-1">
                  Running employee credit: ₹{money(attendanceSummary.runningCreditBalance || 0)}
                </div>
                <div className="small">
                  Eligible staff days: {Number(attendanceSummary.eligibleEmployeeDays || 0)}
                </div>
                <div className="small">
                  Staff count: {Number(attendanceSummary.totalEmployees || 0)}
                </div>
              </div>
            </div>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-md-7">
              <div className="card report-chart-card">
                <div className="card-body">
                  <div className="report-chart-head">
                    <div>
                      <span>Payroll view</span>
                      <h6>Net payable by employee</h6>
                    </div>
                    <small>Top employees for the selected attendance window</small>
                  </div>
                  <div className="report-chart-shell">
                    {attendancePayableChartData.length ? (
                      <ResponsiveContainer>
                        <BarChart data={attendancePayableChartData} margin={{ top: 8, right: 8, left: 0, bottom: 18 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.08)" />
                          <XAxis dataKey="name" tick={{ fill: "#475569", fontSize: 11, fontWeight: 700 }} interval={0} angle={-10} textAnchor="end" height={60} />
                          <YAxis tick={{ fill: "#475569", fontSize: 11, fontWeight: 700 }} tickFormatter={(value) => money(value)} />
                          <Tooltip content={modernTooltip} />
                          <Bar dataKey="value" radius={[10, 10, 0, 0]}>
                            {attendancePayableChartData.map((entry, index) => (
                              <Cell key={`attendance-pay-${index}`} fill={entry.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="text-muted py-5 text-center">No attendance rows available yet.</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-5">
              <div className="card report-chart-card">
                <div className="card-body">
                  <div className="report-chart-head">
                    <div>
                      <span>Shift mix</span>
                      <h6>Present reconciliations by shift</h6>
                    </div>
                  </div>
                  <div className="report-chart-shell">
                    {attendanceShiftChartData.length ? (
                      <ResponsiveContainer>
                        <PieChart>
                          <Pie
                            data={attendanceShiftChartData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={68}
                            outerRadius={102}
                            paddingAngle={4}
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          >
                            {attendanceShiftChartData.map((entry, index) => (
                              <Cell key={`attendance-shift-${index}`} fill={entry.fill} />
                            ))}
                          </Pie>
                          <Tooltip content={modernTooltip} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="text-muted py-5 text-center">No shift attendance to chart.</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-striped table-bordered align-middle">
              <thead className="table-light">
                <tr>
                  <th onDoubleClick={() => handleTableSort("attendance", "employee_name")} title="Double-click to sort" style={{ cursor: "pointer" }}>Employee{sortIndicator("attendance", "employee_name")}</th>
                  <th onDoubleClick={() => handleTableSort("attendance", "role")} title="Double-click to sort" style={{ cursor: "pointer" }}>Role{sortIndicator("attendance", "role")}</th>
                  <th onDoubleClick={() => handleTableSort("attendance", "shift_type")} title="Double-click to sort" style={{ cursor: "pointer" }}>Default Shift{sortIndicator("attendance", "shift_type")}</th>
                  <th onDoubleClick={() => handleTableSort("attendance", "date_of_joining")} title="Double-click to sort" style={{ cursor: "pointer" }}>DOJ{sortIndicator("attendance", "date_of_joining")}</th>
                  <th onDoubleClick={() => handleTableSort("attendance", "eligible_days")} title="Double-click to sort" style={{ cursor: "pointer" }}>Eligible Days{sortIndicator("attendance", "eligible_days")}</th>
                  <th onDoubleClick={() => handleTableSort("attendance", "present_days")} title="Double-click to sort" style={{ cursor: "pointer" }}>Present{sortIndicator("attendance", "present_days")}</th>
                  <th onDoubleClick={() => handleTableSort("attendance", "absent_days")} title="Double-click to sort" style={{ cursor: "pointer" }}>Absent{sortIndicator("attendance", "absent_days")}</th>
                  <th onDoubleClick={() => handleTableSort("attendance", "attendance_rate")} title="Double-click to sort" style={{ cursor: "pointer" }}>Attendance %{sortIndicator("attendance", "attendance_rate")}</th>
                  <th onDoubleClick={() => handleTableSort("attendance", "salary")} title="Double-click to sort" style={{ cursor: "pointer" }}>Salary / Month{sortIndicator("attendance", "salary")}</th>
                  <th onDoubleClick={() => handleTableSort("attendance", "gross_payable")} title="Double-click to sort" style={{ cursor: "pointer" }}>Gross Payable{sortIndicator("attendance", "gross_payable")}</th>
                  <th onDoubleClick={() => handleTableSort("attendance", "credit_deduction")} title="Double-click to sort" style={{ cursor: "pointer" }}>Credit Deduction{sortIndicator("attendance", "credit_deduction")}</th>
                  <th onDoubleClick={() => handleTableSort("attendance", "running_credit_balance")} title="Double-click to sort" style={{ cursor: "pointer" }}>Running Credit{sortIndicator("attendance", "running_credit_balance")}</th>
                  <th onDoubleClick={() => handleTableSort("attendance", "net_payable")} title="Double-click to sort" style={{ cursor: "pointer" }}>Net Payable{sortIndicator("attendance", "net_payable")}</th>
                  <th onDoubleClick={() => handleTableSort("attendance", "last_present_date")} title="Double-click to sort" style={{ cursor: "pointer" }}>Last Present{sortIndicator("attendance", "last_present_date")}</th>
                </tr>
              </thead>
              <tbody>
                {currentAttendanceRows.length === 0 ? (
                  <tr>
                    <td colSpan="14" className="text-center">
                      {attendanceLoading ? "Loading attendance..." : "No attendance rows found"}
                    </td>
                  </tr>
                ) : (
                  currentAttendanceRows.map((row) => (
                    <tr key={`attendance-${row.user_id}`}>
                      <td>{row.employee_name || "-"}</td>
                      <td className="text-capitalize">{row.role || "-"}</td>
                      <td>{row.shift_type || "-"}</td>
                      <td>{row.date_of_joining || "-"}</td>
                      <td>{Number(row.eligible_days || 0)}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-link p-0 text-decoration-none fw-semibold"
                          onClick={() => {
                            setSelectedAttendanceEmployee(row);
                            setShowAttendanceDetail(true);
                          }}
                        >
                          {Number(row.present_days || 0)}
                        </button>
                      </td>
                      <td>{Number(row.absent_days || 0)}</td>
                      <td>{Number(row.attendance_rate || 0).toFixed(2)}%</td>
                      <td>₹{money(row.salary || 0)}</td>
                      <td>₹{money(row.gross_payable || 0)}</td>
                      <td className="text-danger">₹{money(row.credit_deduction || 0)}</td>
                      <td>₹{money(row.running_credit_balance || 0)}</td>
                      <td className="fw-bold text-success">₹{money(row.net_payable || 0)}</td>
                      <td>{row.last_present_date || "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {attendancePageCount > 1 && (
            <div className="d-flex justify-content-center mt-3">
              <ReactPaginate
                previousLabel={"← Prev"}
                nextLabel={"Next →"}
                pageCount={attendancePageCount}
                onPageChange={handleAttendancePageClick}
                containerClassName={"pagination"}
                pageClassName={"page-item"}
                pageLinkClassName={"page-link"}
                previousClassName={"page-item"}
                nextClassName={"page-item"}
                previousLinkClassName={"page-link"}
                nextLinkClassName={"page-link"}
                activeClassName={"active"}
                forcePage={attendancePage}
              />
            </div>
          )}

          {showAttendanceDetail && selectedAttendanceEmployee && (
            <div
              className="report-modal-overlay"
              role="presentation"
              onClick={() => {
                setShowAttendanceDetail(false);
                setSelectedAttendanceEmployee(null);
              }}
            >
              <div
                className="report-modal-card"
                role="dialog"
                aria-modal="true"
                aria-label="Attendance detail"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="report-modal-header">
                  <div>
                    <span>Attendance Detail</span>
                    <h6>
                      {selectedAttendanceEmployee.employee_name} • {selectedAttendanceEmployee.role} • {selectedAttendanceEmployee.shift_type || "-"}
                    </h6>
                  </div>
                  <button
                    type="button"
                    className="report-modal-close"
                    onClick={() => {
                      setShowAttendanceDetail(false);
                      setSelectedAttendanceEmployee(null);
                    }}
                  >
                    ×
                  </button>
                </div>

                <div className="report-modal-body">
                  <div className="row g-2 mb-3">
                    <div className="col-md-3">
                      <div className="report-modal-stat">
                        <span>Present Days</span>
                        <strong>{Number(selectedAttendanceEmployee.present_days || 0)}</strong>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="report-modal-stat">
                        <span>Absent Days</span>
                        <strong>{Number(selectedAttendanceEmployee.absent_days || 0)}</strong>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="report-modal-stat">
                        <span>Attendance %</span>
                        <strong>{Number(selectedAttendanceEmployee.attendance_rate || 0).toFixed(2)}%</strong>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="report-modal-stat">
                        <span>Net Payable</span>
                        <strong>₹{money(selectedAttendanceEmployee.net_payable || 0)}</strong>
                      </div>
                    </div>
                  </div>

                  <div className="table-responsive">
                    <table className="table table-sm table-bordered align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>Date</th>
                          <th>Status</th>
                          <th>Shift</th>
                          <th>Start</th>
                          <th>End</th>
                          <th>Reconciliation IDs</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(selectedAttendanceEmployee.daily_attendance || []).length ? (
                          selectedAttendanceEmployee.daily_attendance.map((dayRow, index) => (
                            <tr key={`${selectedAttendanceEmployee.user_id}-${dayRow.date}-${index}`}>
                              <td>{index + 1}</td>
                              <td>{dayRow.date || "-"}</td>
                              <td className={dayRow.status === "Present" ? "text-success fw-semibold" : "text-danger fw-semibold"}>
                                {dayRow.status}
                              </td>
                              <td>{dayRow.shift || "-"}</td>
                              <td>{dayRow.attendance_start ? formatDateTimeDisplay(dayRow.attendance_start) : "-"}</td>
                              <td>{dayRow.attendance_end ? formatDateTimeDisplay(dayRow.attendance_end) : "-"}</td>
                              <td>{Array.isArray(dayRow.reconciliation_ids) && dayRow.reconciliation_ids.length ? dayRow.reconciliation_ids.join(", ") : "-"}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="7" className="text-center text-muted">
                              No day-wise attendance rows available.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === "recon" && (
        <>
          <div className="row g-2 mb-3">
            {[
              ["Total", reconTotals.total_amount],
              ["Discount", reconTotals.total_discount],
              ["Net Sale", reconTotals.net_sale],
              ["Cash", reconTotals.cash_amount],
              ["UPI", reconTotals.upi_amount],
              ["Card", reconTotals.card_amount],
              ["HP Pay", reconTotals.hp_pay_amount],
              ["Credit", reconTotals.credit_amount],
              ["Test", reconTotals.test_amount],
              ["Others", reconTotals.others_amount],
              ["Fleet", reconTotals.fleet],
              ["Misc", reconTotals.misc],
              ["Remaining", reconTotals.remaining_balance],
            ].map(([label, val]) => {
              const clickable = label === "Credit";
              return (
              <div className="col-6 col-md-4 col-lg-3" key={label}>
                <div
                  className={`card border-secondary h-100 ${clickable ? "shadow-sm" : ""}`}
                  role={clickable ? "button" : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  onClick={clickable ? openPeriodReconSplit : undefined}
                  onKeyDown={
                    clickable
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            openPeriodReconSplit();
                          }
                        }
                      : undefined
                  }
                  style={clickable ? { cursor: "pointer" } : undefined}
                >
                  <div className="card-body py-2">
                    <small className="text-muted d-block">
                      {label}
                      {clickable && (
                        <span className="badge bg-info text-dark ms-2">Split</span>
                      )}
                    </small>
                    <strong
                      className={
                        label === "Discount"
                          ? "text-danger"
                          : label === "Net Sale"
                          ? "text-success"
                          : ""
                      }
                    >
                      ₹{Number(val || 0).toFixed(2)}
                    </strong>
                  </div>
                </div>
              </div>
            )})}
          </div>

          <div className="alert alert-info py-2 mb-3">
            Tip: click the <strong>Credit</strong> summary card for the period split, or click any
            <strong>Total</strong> / <strong>Credit</strong> value in the table to open that
            reconciliation split.
          </div>

          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <div className="card report-chart-card">
                <div className="card-body">
                  <div className="report-chart-head">
                    <div>
                      <span>Settlement mix</span>
                      <h6>Payment mode breakdown</h6>
                    </div>
                  </div>
                  <div className="report-chart-shell">
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          data={reconPieData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={80}
                          outerRadius={112}
                          paddingAngle={4}
                          labelLine={false}
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {reconPieData.map((_, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={reportChartColors[index % reportChartColors.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip content={modernTooltip} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  {reconPieData.length === 0 && (
                    <div className="text-muted">No payment breakup data</div>
                  )}
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="card report-chart-card">
                <div className="card-body">
                  <div className="report-chart-head">
                    <div>
                      <span>Employee view</span>
                      <h6>Employee-wise total amount</h6>
                    </div>
                  </div>
                  <div className="report-chart-shell">
                    <ResponsiveContainer>
                      <BarChart data={reconByEmployee}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.08)" />
                        <XAxis dataKey="employee_name" tick={{ fill: "#475569", fontSize: 12, fontWeight: 700 }} />
                        <YAxis tick={{ fill: "#475569", fontSize: 12, fontWeight: 700 }} tickFormatter={(value) => money(value)} />
                        <Tooltip content={modernTooltip} />
                        <Bar dataKey="total" radius={[12, 12, 4, 4]}>
                          {reconByEmployee.map((_, index) => (
                            <Cell
                              key={`emp-cell-${index}`}
                              fill={reportChartColors[index % reportChartColors.length]}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  {reconByEmployee.length === 0 && (
                    <div className="text-muted">No employee totals</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-striped table-bordered align-middle">
              <thead className="table-light">
                <tr>
                  <th onDoubleClick={() => handleTableSort("recon", "id")} title="Double-click to sort" style={{ cursor: "pointer" }}>ID{sortIndicator("recon", "id")}</th>
                  <th onDoubleClick={() => handleTableSort("recon", "recon_start_datetime")} title="Double-click to sort" style={{ cursor: "pointer" }}>Start Time{sortIndicator("recon", "recon_start_datetime")}</th>
                  <th onDoubleClick={() => handleTableSort("recon", "recon_end_datetime")} title="Double-click to sort" style={{ cursor: "pointer" }}>End Time{sortIndicator("recon", "recon_end_datetime")}</th>
                  <th onDoubleClick={() => handleTableSort("recon", "employee_name")} title="Double-click to sort" style={{ cursor: "pointer" }}>Employee Name{sortIndicator("recon", "employee_name")}</th>
                  <th onDoubleClick={() => handleTableSort("recon", "shift")} title="Double-click to sort" style={{ cursor: "pointer" }}>Shift{sortIndicator("recon", "shift")}</th>
                  <th onDoubleClick={() => handleTableSort("recon", "fuel_total")} title="Double-click to sort" style={{ cursor: "pointer" }}>Fuel Amount{sortIndicator("recon", "fuel_total")}</th>
                  <th onDoubleClick={() => handleTableSort("recon", "lube_total")} title="Double-click to sort" style={{ cursor: "pointer" }}>Lube Amount{sortIndicator("recon", "lube_total")}</th>
                  <th onDoubleClick={() => handleTableSort("recon", "lube_adjustment")} title="Double-click to sort" style={{ cursor: "pointer" }}>Missing Lube{sortIndicator("recon", "lube_adjustment")}</th>
                  <th onDoubleClick={() => handleTableSort("recon", "total_amount")} title="Double-click to sort" style={{ cursor: "pointer" }}>Total{sortIndicator("recon", "total_amount")}</th>
                  <th onDoubleClick={() => handleTableSort("recon", "total_discount")} title="Double-click to sort" style={{ cursor: "pointer" }}>Discount{sortIndicator("recon", "total_discount")}</th>
                  <th onDoubleClick={() => handleTableSort("recon", "net_sale")} title="Double-click to sort" style={{ cursor: "pointer" }}>Net Sale{sortIndicator("recon", "net_sale")}</th>
                  <th onDoubleClick={() => handleTableSort("recon", "cash_amount")} title="Double-click to sort" style={{ cursor: "pointer" }}>Cash{sortIndicator("recon", "cash_amount")}</th>
                  <th onDoubleClick={() => handleTableSort("recon", "upi_amount")} title="Double-click to sort" style={{ cursor: "pointer" }}>UPI{sortIndicator("recon", "upi_amount")}</th>
                  <th onDoubleClick={() => handleTableSort("recon", "card_amount")} title="Double-click to sort" style={{ cursor: "pointer" }}>Card{sortIndicator("recon", "card_amount")}</th>
                  <th onDoubleClick={() => handleTableSort("recon", "hp_pay_amount")} title="Double-click to sort" style={{ cursor: "pointer" }}>HP Pay{sortIndicator("recon", "hp_pay_amount")}</th>
                  <th onDoubleClick={() => handleTableSort("recon", "credit_amount")} title="Double-click to sort" style={{ cursor: "pointer" }}>Credit{sortIndicator("recon", "credit_amount")}</th>
                  <th onDoubleClick={() => handleTableSort("recon", "test_amount")} title="Double-click to sort" style={{ cursor: "pointer" }}>Test{sortIndicator("recon", "test_amount")}</th>
                  <th onDoubleClick={() => handleTableSort("recon", "others_amount")} title="Double-click to sort" style={{ cursor: "pointer" }}>Others{sortIndicator("recon", "others_amount")}</th>
                  <th onDoubleClick={() => handleTableSort("recon", "fleet")} title="Double-click to sort" style={{ cursor: "pointer" }}>Fleet{sortIndicator("recon", "fleet")}</th>
                  <th onDoubleClick={() => handleTableSort("recon", "misc")} title="Double-click to sort" style={{ cursor: "pointer" }}>Misc{sortIndicator("recon", "misc")}</th>
                  <th onDoubleClick={() => handleTableSort("recon", "remaining_balance")} title="Double-click to sort" style={{ cursor: "pointer" }}>Remaining{sortIndicator("recon", "remaining_balance")}</th>
                  <th onDoubleClick={() => handleTableSort("recon", "comment")} title="Double-click to sort" style={{ cursor: "pointer" }}>Comment{sortIndicator("recon", "comment")}</th>
                </tr>
              </thead>
              <tbody>
                {currentReconRows.length === 0 ? (
                  <tr>
                    <td colSpan="22" className="text-center">
                      {reconLoading ? "Loading..." : "No data found"}
                    </td>
                  </tr>
                ) : (
                  currentReconRows.map((r) => (
                    <tr key={r.id}>
                      <td>{r.id}</td>
                      <td>{formatDateTimeDisplay(r.recon_start_datetime || r.recon_date)}</td>
                      <td>{formatDateTimeDisplay(r.recon_end_datetime || r.recon_date)}</td>
                      <td>{r.employee_name ?? "-"}</td>
                      <td>{r.shift ?? "-"}</td>
                      <td>{Number(r.fuel_total || 0).toFixed(2)}</td>
                      <td>{Number(r.lube_total || 0).toFixed(2)}</td>
                      <td className="text-warning fw-semibold">
                        {Number(r.lube_adjustment || 0).toFixed(2)}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-link p-0 align-baseline text-decoration-none fw-semibold"
                          onClick={() => openReconRowSplit(r, "total")}
                        >
                          ₹{Number(r.total_amount || 0).toFixed(2)}
                        </button>
                      </td>
                      <td className="text-danger">
                        {Number(r.total_discount || 0).toFixed(2)}
                      </td>
                      <td className="text-success">
                        {Number(r.net_sale || 0).toFixed(2)}
                      </td>
                      <td>{Number(r.cash_amount || 0).toFixed(2)}</td>
                      <td>{Number(r.upi_amount || 0).toFixed(2)}</td>
                      <td>{Number(r.card_amount || 0).toFixed(2)}</td>
                      <td>{Number(r.hp_pay_amount || 0).toFixed(2)}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-link p-0 align-baseline text-decoration-none fw-semibold"
                          onClick={() => openReconRowSplit(r, "credit")}
                        >
                          ₹{Number(r.credit_amount || 0).toFixed(2)}
                        </button>
                      </td>
                      <td>{Number(r.test_amount || 0).toFixed(2)}</td>
                      <td>{Number(r.others_amount || 0).toFixed(2)}</td>
                      <td>{Number(r.fleet || 0).toFixed(2)}</td>
                      <td>{Number(r.misc || 0).toFixed(2)}</td>
                      <td>{Number(r.remaining_balance || 0).toFixed(2)}</td>
                      <td>{r.comment || ""}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {reconPageCount > 1 && (
            <div className="d-flex justify-content-center mt-3">
              <ReactPaginate
                previousLabel={"← Prev"}
                nextLabel={"Next →"}
                pageCount={reconPageCount}
                onPageChange={handleReconPageClick}
                containerClassName={"pagination"}
                pageClassName={"page-item"}
                pageLinkClassName={"page-link"}
                previousClassName={"page-item"}
                nextClassName={"page-item"}
                previousLinkClassName={"page-link"}
                nextLinkClassName={"page-link"}
                activeClassName={"active"}
              />
            </div>
          )}

          {showReconCreditSplit && (
            <div
              className="report-modal-overlay"
              role="presentation"
              onClick={() => {
                setShowReconCreditSplit(false);
                setSelectedReconSplit(null);
              }}
            >
              <div
                className="report-modal-card"
                role="dialog"
                aria-modal="true"
                aria-label="Reconciliation credit split"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="report-modal-header">
                  <div>
                    <span>
                      {selectedReconSplit ? "Reconciliation Split" : "Reconciliation Credit Split"}
                    </span>
                    <h6>
                      {selectedReconSplit
                        ? `ID ${selectedReconSplit.id} • ${selectedReconSplit.source === "total" ? "Total" : "Credit"} • ${formatReconWindow(
                            selectedReconSplit.recon_start_datetime,
                            selectedReconSplit.recon_end_datetime,
                            formatDateTimeDisplay(selectedReconSplit.recon_date)
                          )} • ${selectedReconSplit.employee_name || "-"}`
                        : "Customer-wise split of reconciled credit amount"}
                    </h6>
                  </div>
                  <button
                    type="button"
                    className="report-modal-close"
                    onClick={() => {
                      setShowReconCreditSplit(false);
                      setSelectedReconSplit(null);
                    }}
                  >
                    ×
                  </button>
                </div>

                <div className="report-modal-body">
                  <div className="row g-2 mb-3">
                    <div className="col-md-4">
                      <div className="report-modal-stat">
                        <span>
                          {selectedReconSplit?.source === "total"
                            ? "Reconciliation Total"
                            : "Total Credit"}
                        </span>
                        <strong>
                          ₹
                          {Number(
                            selectedReconSplit
                              ? selectedReconSplit.total_amount
                              : reconTotals.credit_amount || 0
                          ).toFixed(2)}
                        </strong>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="report-modal-stat">
                        <span>
                          {selectedReconSplit?.source === "total" ? "Fuel Rows" : "Customers"}
                        </span>
                        <strong>{selectedReconSplit?.source === "total" ? selectedReconFuelSplit.length : activeReconCreditSplit.length}</strong>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="report-modal-stat">
                        <span>
                          {selectedReconSplit?.source === "total"
                            ? "Settlement Modes"
                            : selectedReconSplit
                            ? "Rows in split"
                            : "Rows in period"}
                        </span>
                        <strong>
                          {selectedReconSplit?.source === "total"
                            ? selectedReconSettlementSplit.length
                            : selectedReconSplit
                            ? selectedReconSplitRows.length
                            : custCreditRows.length}
                        </strong>
                      </div>
                    </div>
                  </div>

                  {selectedReconSplit?.source === "total" ? (
                    <>
                      <div className="report-modal-chart">
                        {selectedReconFuelSplit.length ? (
                          <ResponsiveContainer width="100%" height={280}>
                            <BarChart
                              data={selectedReconFuelSplit.slice(0, 12)}
                              margin={{ top: 10, right: 16, left: 0, bottom: 10 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.08)" />
                              <XAxis
                                dataKey="fuelType"
                                tick={{ fill: "#475569", fontSize: 11, fontWeight: 700 }}
                                axisLine={{ stroke: "rgba(148,163,184,0.45)" }}
                                tickLine={false}
                                interval={0}
                                height={60}
                              />
                              <YAxis
                                tick={{ fill: "#475569", fontSize: 11, fontWeight: 700 }}
                                axisLine={{ stroke: "rgba(148,163,184,0.45)" }}
                                tickLine={false}
                                tickFormatter={(value) => Number(value || 0).toFixed(0)}
                              />
                              <Tooltip
                                formatter={(value) => Number(value || 0).toFixed(2)}
                                labelStyle={{ color: "#0f172a", fontWeight: 700 }}
                              />
                              <Bar dataKey="volume" radius={[10, 10, 0, 0]}>
                                {selectedReconFuelSplit.slice(0, 12).map((row, index) => (
                                  <Cell key={`fuel-split-${index}`} fill={row.fill} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        ) : reconSplitLoading ? (
                          <div className="text-muted py-4 text-center">Loading split details...</div>
                        ) : (
                          <div className="text-muted py-4 text-center">
                            No fuel split rows were recorded for this reconciliation.
                          </div>
                        )}
                      </div>

                      <div className="table-responsive mt-3">
                        <table className="table table-sm table-bordered align-middle">
                          <thead className="table-light">
                            <tr>
                              <th>#</th>
                              <th>Fuel Type</th>
                              <th className="text-end">Rows</th>
                              <th className="text-end">Volume (L)</th>
                              <th className="text-end">Testing (L)</th>
                              <th className="text-end">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedReconFuelSplit.length ? (
                              selectedReconFuelSplit.map((row, index) => (
                                <tr key={`${row.fuelType}-${index}`}>
                                  <td>{index + 1}</td>
                                  <td>{row.fuelType}</td>
                                  <td className="text-end">{row.count}</td>
                                  <td className="text-end">{Number(row.volume || 0).toFixed(3)}</td>
                                  <td className="text-end">{Number(row.testingVolume || 0).toFixed(3)}</td>
                                  <td className="text-end">₹{Number(row.amount || 0).toFixed(2)}</td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="6" className="text-center text-muted">
                                  No fuel split rows available.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      <div className="table-responsive mt-3">
                        <table className="table table-sm table-bordered align-middle">
                          <thead className="table-light">
                            <tr>
                              <th>#</th>
                              <th>Settlement Mode</th>
                              <th className="text-end">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedReconSettlementSplit.length ? (
                              selectedReconSettlementSplit.map((row, index) => (
                                <tr key={`${row.label}-${index}`}>
                                  <td>{index + 1}</td>
                                  <td>{row.label}</td>
                                  <td className="text-end">₹{Number(row.value || 0).toFixed(2)}</td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="3" className="text-center text-muted">
                                  No settlement rows available.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="report-modal-chart">
                        {activeReconCreditSplit.length ? (
                          <ResponsiveContainer width="100%" height={280}>
                            <BarChart
                              data={activeReconCreditSplit.slice(0, 12)}
                              margin={{ top: 10, right: 16, left: 0, bottom: 10 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.08)" />
                              <XAxis
                                dataKey="name"
                                tick={{ fill: "#475569", fontSize: 11, fontWeight: 700 }}
                                axisLine={{ stroke: "rgba(148,163,184,0.45)" }}
                                tickLine={false}
                                interval={0}
                                height={60}
                              />
                              <YAxis
                                tick={{ fill: "#475569", fontSize: 11, fontWeight: 700 }}
                                axisLine={{ stroke: "rgba(148,163,184,0.45)" }}
                                tickLine={false}
                                tickFormatter={(value) => `₹${Number(value || 0).toFixed(0)}`}
                              />
                              <Tooltip
                                formatter={(value) => `₹${Number(value || 0).toFixed(2)}`}
                                labelStyle={{ color: "#0f172a", fontWeight: 700 }}
                              />
                              <Bar dataKey="amount" radius={[10, 10, 0, 0]}>
                                {activeReconCreditSplit.slice(0, 12).map((row, index) => (
                                  <Cell key={`recon-credit-${index}`} fill={row.fill} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        ) : reconSplitLoading ? (
                          <div className="text-muted py-4 text-center">Loading split details...</div>
                        ) : (
                          <div className="text-muted py-4 text-center">
                            No customer split rows were recorded in the selected period.
                          </div>
                        )}
                      </div>

                      <div className="table-responsive mt-3">
                        <table className="table table-sm table-bordered align-middle">
                          <thead className="table-light">
                            <tr>
                              <th>#</th>
                              <th>Customer</th>
                              <th>Fuel Type</th>
                              <th className="text-end">Volume (L)</th>
                              <th className="text-end">Amount</th>
                              <th>Comment</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(selectedReconSplit ? selectedReconSplitRows : reconCreditCustomerSplit).length ? (
                              (selectedReconSplit ? selectedReconSplitRows : reconCreditCustomerSplit).map((row, index) => (
                                <tr key={`${row.id || row.customer_id || row.name || index}`}>
                                  <td>{index + 1}</td>
                                  <td>{row.customer_name || row.name}</td>
                                  <td>{row.fuel_type || row.fuelType || "-"}</td>
                                  <td className="text-end">{Number(row.volume || 0).toFixed(3)}</td>
                                  <td className="text-end">₹{Number(row.amount || 0).toFixed(2)}</td>
                                  <td>{stripHtml(row.credit_comment || row.comment || "-") || "-"}</td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="6" className="text-center text-muted">
                                  {reconSplitLoading ? "Loading split details..." : "No split rows available."}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === "custcredit" && (
        <>
          <div className="row g-2 mb-3">
            {[
              ["Total Credit", ccTotals.total_amount],
              ["Total Paid", ccTotals.total_paid],
              ["Total Pending", ccTotals.total_pending],
              ["Avg Creditor Days", ccTotals.avg_days],
            ].map(([label, val]) => (
              <div className="col-6 col-md-4 col-lg-3" key={label}>
                <div className="card border-secondary h-100">
                  <div className="card-body py-2">
                    <small className="text-muted d-block">{label}</small>
                    <strong>
                      {label.includes("Days")
                        ? `${val}`
                        : `₹${Number(val || 0).toFixed(2)}`}
                    </strong>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <div className="card report-chart-card">
                <div className="card-body">
                  <div className="report-chart-head">
                    <div>
                      <span>Customer credit</span>
                      <h6>Highest credit customers by amount</h6>
                    </div>
                  </div>
                  <div className="report-chart-shell">
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          data={ccByCustomerAmount.slice(0, 8)}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={78}
                          outerRadius={112}
                          paddingAngle={4}
                          labelLine={false}
                          label={({ name, percent }) => `${name.slice(0, 8)} ${(percent * 100).toFixed(0)}%`}
                        >
                          {ccByCustomerAmount.slice(0, 8).map((_, index) => (
                            <Cell
                              key={`ccpie-${index}`}
                              fill={reportChartColors[index % reportChartColors.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip content={modernTooltip} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  {ccByCustomerAmount.length === 0 && (
                    <div className="text-muted">No credit data</div>
                  )}
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="card report-chart-card">
                <div className="card-body">
                  <div className="report-chart-head">
                    <div>
                      <span>Credit ageing</span>
                      <h6>Average creditor days by customer</h6>
                    </div>
                  </div>
                  <div className="report-chart-shell">
                    <ResponsiveContainer>
                      <BarChart data={ccByCustomerDays.slice(0, 12)}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.08)" />
                        <XAxis dataKey="customer" hide />
                        <YAxis tick={{ fill: "#475569", fontSize: 12, fontWeight: 700 }} />
                        <Tooltip content={modernTooltip} />
                        <Legend />
                        <Bar dataKey="avg_days" name="Avg Days" radius={[12, 12, 4, 4]}>
                          {ccByCustomerDays.slice(0, 12).map((_, index) => (
                            <Cell
                              key={`days-cell-${index}`}
                              fill={reportChartColors[index % reportChartColors.length]}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <small className="text-muted">
                    Showing top 12 customers by highest avg creditor days
                  </small>
                  {ccByCustomerDays.length === 0 && (
                    <div className="text-muted">No creditor-days data</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-striped table-bordered align-middle">
              <thead className="table-light">
                <tr>
                  <th onDoubleClick={() => handleTableSort("custcredit", "customer_name")} title="Double-click to sort" style={{ cursor: "pointer" }}>Customer{sortIndicator("custcredit", "customer_name")}</th>
                  <th onDoubleClick={() => handleTableSort("custcredit", "fuel_type")} title="Double-click to sort" style={{ cursor: "pointer" }}>Fuel Type{sortIndicator("custcredit", "fuel_type")}</th>
                  <th onDoubleClick={() => handleTableSort("custcredit", "volume")} title="Double-click to sort" style={{ cursor: "pointer" }}>Volume (L){sortIndicator("custcredit", "volume")}</th>
                  <th onDoubleClick={() => handleTableSort("custcredit", "amount")} title="Double-click to sort" style={{ cursor: "pointer" }}>Credit Amount (₹){sortIndicator("custcredit", "amount")}</th>
                  <th onDoubleClick={() => handleTableSort("custcredit", "pending_amount")} title="Double-click to sort" style={{ cursor: "pointer" }}>Pending (₹){sortIndicator("custcredit", "pending_amount")}</th>
                  <th onDoubleClick={() => handleTableSort("custcredit", "total_paid")} title="Double-click to sort" style={{ cursor: "pointer" }}>Total Paid (₹){sortIndicator("custcredit", "total_paid")}</th>
                  <th onDoubleClick={() => handleTableSort("custcredit", "credit_status")} title="Double-click to sort" style={{ cursor: "pointer" }}>Status{sortIndicator("custcredit", "credit_status")}</th>
                  <th onDoubleClick={() => handleTableSort("custcredit", "credit_comment")} title="Double-click to sort" style={{ cursor: "pointer" }}>Credit Comment{sortIndicator("custcredit", "credit_comment")}</th>
                  <th onDoubleClick={() => handleTableSort("custcredit", "creditCreated")} title="Double-click to sort" style={{ cursor: "pointer" }}>Credit Created{sortIndicator("custcredit", "creditCreated")}</th>
                  <th onDoubleClick={() => handleTableSort("custcredit", "latestPaymentDate")} title="Double-click to sort" style={{ cursor: "pointer" }}>Latest Payment Date{sortIndicator("custcredit", "latestPaymentDate")}</th>
                  <th onDoubleClick={() => handleTableSort("custcredit", "latest_payment_mode")} title="Double-click to sort" style={{ cursor: "pointer" }}>Latest Mode{sortIndicator("custcredit", "latest_payment_mode")}</th>
                  <th onDoubleClick={() => handleTableSort("custcredit", "latest_reference_no")} title="Double-click to sort" style={{ cursor: "pointer" }}>Latest Ref{sortIndicator("custcredit", "latest_reference_no")}</th>
                  <th onDoubleClick={() => handleTableSort("custcredit", "latest_payment_comment")} title="Double-click to sort" style={{ cursor: "pointer" }}>Latest Payment Comment{sortIndicator("custcredit", "latest_payment_comment")}</th>
                  <th onDoubleClick={() => handleTableSort("custcredit", "creditor_days")} title="Double-click to sort" style={{ cursor: "pointer" }}>Creditor Days{sortIndicator("custcredit", "creditor_days")}</th>
                </tr>
              </thead>
              <tbody>
                {currentCustomerCredits.length === 0 ? (
                  <tr>
                    <td colSpan="14" className="text-center">
                      {custCreditLoading ? "Loading..." : "No data found"}
                    </td>
                  </tr>
                ) : (
                  currentCustomerCredits.map((r, idx) => (
                    <tr key={`${r.id}-${idx}`}>
                      <td>{r.customer_name || "-"}</td>
                      <td>{r.fuel_type || "-"}</td>
                      <td>{Number(r.volume || 0).toFixed(2)}</td>
                      <td>{Number(r.amount || 0).toFixed(2)}</td>
                      <td>{Number(r.pending_amount || 0).toFixed(2)}</td>
                      <td>{Number(r.total_paid || 0).toFixed(2)}</td>
                      <td>{r.credit_status || "-"}</td>
                      <td>{r.credit_comment || ""}</td>
                      <td>
                        {r.credit_created_at
                          ? moment(r.credit_created_at).format("YYYY-MM-DD HH:mm")
                          : "-"}
                      </td>
                      <td>
                        {r.latest_payment_date
                          ? moment(r.latest_payment_date).format("YYYY-MM-DD")
                          : "-"}
                      </td>
                      <td>{r.latest_payment_mode || "-"}</td>
                      <td>{r.latest_reference_no || "-"}</td>
                      <td>{r.latest_payment_comment || ""}</td>
                      <td>{Number(r.creditor_days || 0)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {ccPageCount > 1 && (
            <div className="d-flex justify-content-center mt-3">
              <ReactPaginate
                previousLabel={"← Prev"}
                nextLabel={"Next →"}
                pageCount={ccPageCount}
                onPageChange={handleCcPageClick}
                containerClassName={"pagination"}
                pageClassName={"page-item"}
                pageLinkClassName={"page-link"}
                previousClassName={"page-item"}
                nextClassName={"page-item"}
                previousLinkClassName={"page-link"}
                nextLinkClassName={"page-link"}
                activeClassName={"active"}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default NozzleReport;
